// src/payload-guard.ts
var MAX_REPORT_PAYLOAD_BYTES = 4 * 1024 * 1024;
var MAX_SCREENSHOT_DATA_URL_BYTES = 1.5 * 1024 * 1024;
function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}
function estimateJsonBytes(value) {
  const json = safeStringify(value);
  return json === null ? Number.MAX_SAFE_INTEGER : new TextEncoder().encode(json).length;
}
function checkReportPayloadSize(payload, maxBytes = MAX_REPORT_PAYLOAD_BYTES) {
  const json = safeStringify(payload);
  if (json === null) {
    return {
      ok: false,
      bytes: 0,
      maxBytes,
      serializeFailed: true,
      reason: "Report could not be serialized (circular reference in metadata?)"
    };
  }
  const bytes = new TextEncoder().encode(json).length;
  if (bytes <= maxBytes) {
    return { ok: true, bytes, maxBytes };
  }
  return {
    ok: false,
    bytes,
    maxBytes,
    reason: `Report payload ${formatBytes(bytes)} exceeds limit ${formatBytes(maxBytes)}`
  };
}
function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// src/digest.ts
function getSubtle() {
  try {
    const g = globalThis;
    const crypto2 = g["crypto"];
    const subtle = crypto2?.subtle;
    if (subtle && typeof subtle.digest === "function" && typeof subtle.importKey === "function" && typeof subtle.sign === "function") {
      return subtle;
    }
  } catch {
  }
  return null;
}
function bytesToHex(bytes) {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}
async function sha256Hex(value) {
  const subtle = getSubtle();
  if (subtle) {
    const buffer = await subtle.digest("SHA-256", new TextEncoder().encode(value));
    return bytesToHex(new Uint8Array(buffer));
  }
  const { sha256 } = await import('@noble/hashes/sha2.js');
  return bytesToHex(sha256(new TextEncoder().encode(value)));
}
async function hmacSha256Hex(secret, value) {
  const subtle = getSubtle();
  if (subtle) {
    const key = await subtle.importKey(
      "raw",
      new TextEncoder().encode(secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );
    const buffer = await subtle.sign("HMAC", key, new TextEncoder().encode(value));
    return bytesToHex(new Uint8Array(buffer));
  }
  const { hmac } = await import('@noble/hashes/hmac.js');
  const { sha256 } = await import('@noble/hashes/sha2.js');
  const result = hmac(sha256, new TextEncoder().encode(secret), new TextEncoder().encode(value));
  return bytesToHex(result);
}

// src/unload-transport.ts
var MUSHI_BEACON_MAX_BYTES = 60 * 1024;
function isMushiBeaconEnvelope(value) {
  if (!value || typeof value !== "object") return false;
  const rec = value;
  return rec.mushiBeacon === true && typeof rec.path === "string" && rec.headers !== null && typeof rec.headers === "object";
}
function buildBeaconEnvelope(opts) {
  return {
    mushiBeacon: true,
    path: opts.path,
    headers: opts.headers,
    body: opts.body
  };
}
function isSameOriginOrRelative(url, origin = typeof location !== "undefined" ? location.origin : "") {
  if (!url) return false;
  if (url.startsWith("/")) return true;
  if (!origin) return false;
  try {
    return new URL(url, origin).origin === origin;
  } catch {
    return false;
  }
}
function extractTunnelPath(url) {
  try {
    const parsed = url.startsWith("/") ? new URL(url, "https://tunnel.local") : new URL(url);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}
function sendOnUnload(opts) {
  const fetchImpl = opts.fetchImpl ?? (typeof fetch === "function" ? fetch : void 0);
  if (fetchImpl) {
    try {
      void fetchImpl(opts.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...opts.headers
        },
        body: opts.body,
        keepalive: true
      });
      return true;
    } catch {
    }
  }
  const beacon = opts.sendBeacon ?? (typeof navigator !== "undefined" ? navigator.sendBeacon?.bind(navigator) : void 0);
  if (!beacon || !isSameOriginOrRelative(opts.url)) return false;
  let parsedBody = opts.body;
  try {
    parsedBody = JSON.parse(opts.body);
  } catch {
    parsedBody = opts.body;
  }
  const envelope = JSON.stringify(
    buildBeaconEnvelope({
      path: opts.path ?? extractTunnelPath(opts.url),
      headers: opts.headers,
      body: parsedBody
    })
  );
  if (envelope.length > MUSHI_BEACON_MAX_BYTES) return false;
  try {
    return beacon(opts.url, new Blob([envelope], { type: "application/json" }));
  } catch {
    return false;
  }
}
var pageUnloading = false;
function markPageUnloading() {
  pageUnloading = true;
}
function isPageUnloading() {
  return pageUnloading;
}

// src/api-client.ts
var lastOutbound = null;
function flushLastOutboundOnUnload() {
  if (!lastOutbound) return false;
  return sendOnUnload(lastOutbound);
}
var _credWarningEmitted = false;
var MUSHI_USER_TOKEN_HEADER = "X-Mushi-User-Token";
var MUSHI_PROJECT_HEADER = "X-Mushi-Project";
var MUSHI_SDK_PACKAGE_HEADER = "X-Mushi-SDK-Package";
var MUSHI_SDK_VERSION_HEADER = "X-Mushi-SDK-Version";
function buildSdkIngestHeaders(opts) {
  const headers = {
    "Content-Type": "application/json",
    "X-Mushi-Api-Key": opts.apiKey,
    [MUSHI_PROJECT_HEADER]: opts.projectId
  };
  if (opts.sdkPackage) headers[MUSHI_SDK_PACKAGE_HEADER] = opts.sdkPackage;
  if (opts.sdkVersion) headers[MUSHI_SDK_VERSION_HEADER] = opts.sdkVersion;
  if (opts.userToken) headers[MUSHI_USER_TOKEN_HEADER] = opts.userToken;
  if (opts.internalKind) headers[MUSHI_INTERNAL_HEADER] = opts.internalKind;
  return headers;
}
var DEFAULT_API_ENDPOINT = "https://dxptnwrhwsqckaftyymj.supabase.co/functions/v1/api";
var MUSHI_INTERNAL_HEADER = "X-Mushi-Internal";
var MUSHI_INTERNAL_INIT_MARKER = "__mushiInternal";
function resolveRequestBaseUrl(apiEndpoint, tunnel) {
  const trimmedTunnel = tunnel?.trim();
  if (trimmedTunnel) return trimmedTunnel.replace(/\/$/, "");
  return (apiEndpoint ?? DEFAULT_API_ENDPOINT).replace(/\/$/, "");
}
var DEFAULT_TIMEOUT = 1e4;
var DEFAULT_MAX_RETRIES = 2;
var DEFAULT_CIRCUIT_THRESHOLD = 4;
var DEFAULT_CIRCUIT_COOLDOWN_MS = 3e4;
var MAX_INLINE_RETRY_WAIT_MS = 1e4;
function createApiClient(options) {
  const {
    projectId,
    apiKey,
    apiEndpoint = DEFAULT_API_ENDPOINT,
    tunnel,
    timeout = DEFAULT_TIMEOUT,
    maxRetries = DEFAULT_MAX_RETRIES,
    getUserToken,
    sdkPackage,
    sdkVersion,
    circuitBreaker
  } = options;
  let baseUrl = resolveRequestBaseUrl(apiEndpoint, tunnel);
  const cbEnabled = circuitBreaker?.enabled !== false;
  const cbThreshold = Math.max(1, circuitBreaker?.threshold ?? DEFAULT_CIRCUIT_THRESHOLD);
  const cbCooldownMs = Math.max(1e3, circuitBreaker?.cooldownMs ?? DEFAULT_CIRCUIT_COOLDOWN_MS);
  let cbFailures = 0;
  let cbOpenUntil = 0;
  const cbIsOpen = () => cbEnabled && cbOpenUntil > Date.now();
  const cbRecordReachable = () => {
    cbFailures = 0;
    cbOpenUntil = 0;
  };
  const cbRecordUnreachable = () => {
    if (!cbEnabled) return;
    cbFailures += 1;
    if (cbFailures >= cbThreshold) cbOpenUntil = Date.now() + cbCooldownMs;
  };
  async function request(method, path, body, retries = maxRetries, internalKind, extraHeaders) {
    const tracked = internalKind !== "reporter-poll";
    if (tracked && cbIsOpen()) {
      return { ok: false, error: { code: "CIRCUIT_OPEN", message: "Endpoint temporarily unavailable; retrying later." } };
    }
    const url = `${baseUrl}${path}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    const userToken = getUserToken?.() ?? null;
    const headers = {
      ...buildSdkIngestHeaders({
        apiKey,
        projectId,
        sdkPackage,
        sdkVersion,
        userToken,
        internalKind
      }),
      ...extraHeaders
    };
    const serialized = body ? JSON.stringify(body) : void 0;
    if (serialized && method !== "GET" && internalKind !== "reporter-poll") {
      lastOutbound = { url, headers, body: serialized, path };
    }
    try {
      const response = await fetch(url, {
        method,
        headers,
        body: serialized,
        keepalive: isPageUnloading(),
        signal: controller.signal,
        ...internalKind ? { [MUSHI_INTERNAL_INIT_MARKER]: internalKind } : {}
      });
      clearTimeout(timer);
      if (response.status === 307 || response.status === 308) {
        const target = response.headers.get("Location");
        if (target && retries > 0) {
          const targetBase = target.replace(/\/v1\/.*$/, "").replace(/\/$/, "");
          if (targetBase !== baseUrl) {
            baseUrl = targetBase;
            return request(method, path, body, retries - 1, internalKind, extraHeaders);
          }
        }
      }
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        if ((response.status === 401 || response.status === 403) && !_credWarningEmitted) {
          _credWarningEmitted = true;
          const where = baseUrl === DEFAULT_API_ENDPOINT ? "Get the correct values at: https://kensaur.us/mushi-mushi/admin/projects" : "Get the correct values from your Mushi console's Projects page.";
          console.error(
            `[Mushi] Credentials rejected (HTTP ${response.status}). Check your Project ID and API key scope (must be "report:write"). ` + where
          );
        }
        const rateLimited = response.status === 429;
        const unusable = response.status >= 500 || rateLimited;
        const retryAfterMs = rateLimited ? parseRetryAfter(response.headers.get("Retry-After")) : null;
        const canWaitInline = retryAfterMs === null || retryAfterMs <= MAX_INLINE_RETRY_WAIT_MS;
        if (unusable && retries > 0 && canWaitInline) {
          await sleep(retryAfterMs ?? getBackoffDelay(maxRetries - retries));
          return request(method, path, body, retries - 1, internalKind, extraHeaders);
        }
        if (tracked) {
          if (unusable) cbRecordUnreachable();
          else cbRecordReachable();
        }
        return {
          ok: false,
          error: {
            code: errorBody.error?.code || `HTTP_${response.status}`,
            message: errorBody.error?.message || errorBody.message || `HTTP ${response.status} error`,
            status: response.status
          }
        };
      }
      const payload = await response.json();
      const data = payload && typeof payload === "object" && "ok" in payload && "data" in payload ? payload.data : payload;
      if (tracked) cbRecordReachable();
      return { ok: true, data };
    } catch (error) {
      clearTimeout(timer);
      if (retries > 0 && isRetryable(error)) {
        await sleep(getBackoffDelay(maxRetries - retries));
        return request(method, path, body, retries - 1, internalKind, extraHeaders);
      }
      if (tracked) cbRecordUnreachable();
      return {
        ok: false,
        error: {
          code: "NETWORK_ERROR",
          message: error instanceof Error ? error.message : "Unknown network error"
        }
      };
    }
  }
  async function requestForReporter(method, path, reporterToken, body) {
    try {
      const tokenHash = await sha256Hex(reporterToken);
      const ts = String(Date.now());
      return await request(method, path, body, maxRetries, "reporter-poll", {
        "X-Reporter-Token-Hash": tokenHash,
        "X-Reporter-Ts": ts,
        "X-Reporter-Hmac": await hmacSha256Hex(apiKey, `${projectId}.${ts}.${tokenHash}`)
      });
    } catch (error) {
      return { ok: false, error: { code: "NETWORK_ERROR", message: error instanceof Error ? error.message : "Unknown error" } };
    }
  }
  return {
    async submitReport(report) {
      const guard = checkReportPayloadSize(report);
      if (!guard.ok) {
        return {
          ok: false,
          error: {
            code: guard.serializeFailed ? "SERIALIZE_FAILED" : "PAYLOAD_TOO_LARGE",
            message: guard.reason ?? "Report payload exceeds size limit"
          }
        };
      }
      return request("POST", "/v1/reports", report, maxRetries, "report-submit");
    },
    async getReportStatus(reportId) {
      return request("GET", `/v1/reports/${reportId}/status`, void 0, maxRetries, "report-status");
    },
    async getSdkConfig() {
      return request("GET", "/v1/sdk/config", void 0, maxRetries, "sdk-config");
    },
    async askAssistant(input) {
      return request(
        "POST",
        "/v1/sdk/assistant",
        {
          message: input.message,
          threadId: input.threadId ?? null,
          context: input.context ?? null
        },
        1,
        "community"
      );
    },
    async getLatestSdkVersion(packageName) {
      const query = new URLSearchParams({ package: packageName }).toString();
      return request("GET", `/v1/sdk/latest-version?${query}`, void 0, maxRetries, "sdk-config");
    },
    async postDiscoveryEvent(event) {
      return request(
        "POST",
        "/v1/sdk/discovery",
        event,
        1,
        "discovery"
      );
    },
    async listReporterReports(reporterToken) {
      return requestForReporter("GET", "/v1/reporter/reports", reporterToken);
    },
    reporterRequest: requestForReporter,
    async listReporterComments(reportId, reporterToken) {
      return requestForReporter(
        "GET",
        `/v1/reporter/reports/${reportId}/comments`,
        reporterToken
      );
    },
    async replyToReporterReport(reportId, reporterToken, body, feedbackSignal) {
      return requestForReporter(
        "POST",
        `/v1/reporter/reports/${reportId}/reply`,
        reporterToken,
        { body, ...feedbackSignal ? { feedback_signal: feedbackSignal } : {} }
      );
    },
    async reopenReporterReport(reportId, reporterToken, note) {
      return requestForReporter(
        "POST",
        `/v1/reporter/reports/${reportId}/reopen`,
        reporterToken,
        { note: note ?? "" }
      );
    },
    async listNotifications(reporterToken, opts) {
      const qs = new URLSearchParams();
      if (opts?.since) qs.set("since", opts.since);
      if (opts?.limit) qs.set("limit", String(opts.limit));
      const suffix = qs.size ? `?${qs.toString()}` : "";
      return requestForReporter(
        "GET",
        `/v1/notifications${suffix}`,
        reporterToken
      );
    },
    async markNotificationRead(notificationId, reporterToken) {
      return requestForReporter(
        "POST",
        `/v1/notifications/${notificationId}/read`,
        reporterToken,
        {}
      );
    },
    async listReporterFeatureBoard(reporterToken) {
      return requestForReporter(
        "GET",
        "/v1/reporter/feature-board",
        reporterToken
      );
    },
    async voteReporterFeatureBoard(requestId, reporterToken) {
      return requestForReporter(
        "POST",
        `/v1/reporter/feature-board/${requestId}/vote`,
        reporterToken,
        {}
      );
    },
    // ─── Rewards program (P1) ──────────────────────────────────
    /** POST /v1/sdk/session — lightweight session lifecycle events.
     *  Best-effort (1 retry, no offline queue) — mirrors postDiscoveryEvent. */
    async postSessionEvent(payload) {
      return request(
        "POST",
        "/v1/sdk/session",
        payload,
        1,
        "discovery"
      );
    },
    /** POST /v1/sdk/events — batched product-analytics events (Mushi.track()).
     *  Best-effort (1 retry); the event tracker spills unsent batches to
     *  localStorage and replays them on the next page load. */
    async postProductEvents(payload) {
      return request(
        "POST",
        "/v1/sdk/events",
        payload,
        1,
        "discovery"
      );
    },
    async submitActivity(userId, events, opts) {
      return request(
        "POST",
        "/v1/sdk/activity",
        {
          user_id: userId,
          user_traits: opts?.userTraits,
          opted_in: opts?.optedIn,
          reporter_token_hash: opts?.reporterTokenHash,
          // P2: JWT for monetary verification; omitted when null
          ...opts?.hostJwt ? { host_jwt: opts.hostJwt } : {},
          events
        },
        1,
        // best-effort, 1 retry
        "discovery"
      );
    },
    async getMyPoints(userId) {
      return request(
        "GET",
        `/v1/sdk/me/points?userId=${encodeURIComponent(userId)}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    async getMyTier(userId) {
      return request(
        "GET",
        `/v1/sdk/me/tier?userId=${encodeURIComponent(userId)}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    async getMyHistory(userId, opts) {
      const qs = new URLSearchParams({ userId, ...opts?.limit ? { limit: String(opts.limit) } : {} });
      return request(
        "GET",
        `/v1/sdk/me/history?${qs}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    async getHallOfFame(limit = 10) {
      return request(
        "GET",
        `/v1/sdk/hall-of-fame?limit=${limit}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    // ─── Cross-app community (in-widget tester identity) ──────────
    async sendMagicLink(email) {
      return request(
        "POST",
        "/v1/tester/magic-link",
        { email },
        1,
        "community"
      );
    },
    async linkReporterToken(reporterTokenHash, jwt) {
      return request(
        "POST",
        "/v1/tester/link-reporter",
        { reporter_token_hash: reporterTokenHash },
        1,
        "community",
        { Authorization: `Bearer ${jwt}` }
      );
    },
    async getCrossAppReports(jwt, opts) {
      const qs = new URLSearchParams();
      if (opts?.limit) qs.set("limit", String(opts.limit));
      if (opts?.offset) qs.set("offset", String(opts.offset));
      const suffix = qs.size ? `?${qs.toString()}` : "";
      return request(
        "GET",
        `/v1/tester/cross-app-reports${suffix}`,
        void 0,
        1,
        "community",
        { Authorization: `Bearer ${jwt}` }
      );
    },
    async getMyReputation(jwt) {
      return request(
        "GET",
        "/v1/tester/reputation",
        void 0,
        1,
        "community",
        { Authorization: `Bearer ${jwt}` }
      );
    },
    async getPublicLeaderboard(limit = 20) {
      return request(
        "GET",
        `/v1/public/tester-leaderboard?limit=${limit}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    async getTesterStatus(jwt) {
      return request(
        "GET",
        "/v1/me/tester-status",
        void 0,
        1,
        "community",
        { Authorization: `Bearer ${jwt}` }
      );
    }
  };
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
function getBackoffDelay(attempt) {
  return Math.min(1e3 * 2 ** attempt + Math.random() * 500, 1e4);
}
function parseRetryAfter(value, now3 = Date.now()) {
  if (!value) return null;
  const raw = value.trim();
  if (!raw) return null;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return seconds < 0 ? null : seconds * 1e3;
  const at = Date.parse(raw);
  if (Number.isNaN(at)) return null;
  return Math.max(at - now3, 0);
}
function isRetryable(error) {
  if (error instanceof DOMException && error.name === "AbortError") return true;
  if (error instanceof TypeError) return true;
  return false;
}

// src/error-filters.ts
function matchesErrorFilter(value, filters) {
  if (!value || !filters || filters.length === 0) return false;
  for (const filter of filters) {
    if (typeof filter === "string") {
      if (value.includes(filter)) return true;
      continue;
    }
    try {
      if (filter.test(value)) return true;
    } catch {
    }
  }
  return false;
}
function shouldDropCapturedError(input) {
  if (matchesErrorFilter(input.message, input.ignoreErrors)) return true;
  const filename = input.filename?.trim() || "";
  if (filename && matchesErrorFilter(filename, input.denyUrls)) return true;
  if (input.allowUrls && input.allowUrls.length > 0) {
    if (!filename) return true;
    if (!matchesErrorFilter(filename, input.allowUrls)) return true;
  }
  return false;
}

// src/region.ts
var REGION_ENDPOINTS = {
  us: DEFAULT_API_ENDPOINT,
  eu: DEFAULT_API_ENDPOINT,
  jp: DEFAULT_API_ENDPOINT
};
var ROUTING_CACHE_KEY = "mushi_region_v1";
var ROUTING_CACHE_TTL_MS = 24 * 60 * 60 * 1e3;
async function resolveRegionEndpoint(opts) {
  const explicit = opts.region;
  if (explicit && explicit !== "self" && REGION_ENDPOINTS[explicit]) {
    return REGION_ENDPOINTS[explicit];
  }
  const storage = opts.storage ?? safeLocalStorage();
  const cached = readCache(storage, opts.projectId);
  if (cached) return cached;
  try {
    const fetcher = opts.fetcher ?? fetch;
    const url = `${opts.apiEndpoint.replace(/\/$/, "")}/v1/region/resolve?project_id=${encodeURIComponent(opts.projectId)}`;
    const res = await fetcher(url, { method: "GET" });
    if (!res.ok) return opts.apiEndpoint;
    const body = await res.json();
    if (!body.region || !body.endpoint) return opts.apiEndpoint;
    writeCache(storage, opts.projectId, { region: body.region, endpoint: body.endpoint, ts: Date.now() });
    return body.endpoint;
  } catch {
    return opts.apiEndpoint;
  }
}
function safeLocalStorage() {
  try {
    return typeof globalThis !== "undefined" && "localStorage" in globalThis ? globalThis.localStorage : void 0;
  } catch {
    return void 0;
  }
}
function readCache(storage, projectId) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(`${ROUTING_CACHE_KEY}:${projectId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > ROUTING_CACHE_TTL_MS) return null;
    return parsed.endpoint;
  } catch {
    return null;
  }
}
function writeCache(storage, projectId, entry) {
  if (!storage) return;
  try {
    storage.setItem(`${ROUTING_CACHE_KEY}:${projectId}`, JSON.stringify(entry));
  } catch {
  }
}

// src/env-config.ts
function first(...values) {
  return values.find((v) => v !== void 0 && v !== "");
}
function tryImportMetaEnv(key) {
  try {
    const env = import.meta.env;
    return typeof env === "object" && env !== null ? env[key] : void 0;
  } catch {
    return void 0;
  }
}
function readProcessEnv(read) {
  try {
    return read();
  } catch {
    return void 0;
  }
}
function resolveEnvConfig() {
  const projectId = first(
    tryImportMetaEnv("VITE_MUSHI_PROJECT_ID"),
    tryImportMetaEnv("NEXT_PUBLIC_MUSHI_PROJECT_ID"),
    tryImportMetaEnv("NUXT_PUBLIC_MUSHI_PROJECT_ID"),
    tryImportMetaEnv("EXPO_PUBLIC_MUSHI_PROJECT_ID"),
    readProcessEnv(() => process.env.NEXT_PUBLIC_MUSHI_PROJECT_ID),
    readProcessEnv(() => process.env.EXPO_PUBLIC_MUSHI_PROJECT_ID),
    readProcessEnv(() => process.env.MUSHI_PROJECT_ID)
  );
  const apiKey = first(
    tryImportMetaEnv("VITE_MUSHI_API_KEY"),
    tryImportMetaEnv("NEXT_PUBLIC_MUSHI_API_KEY"),
    tryImportMetaEnv("NUXT_PUBLIC_MUSHI_API_KEY"),
    tryImportMetaEnv("EXPO_PUBLIC_MUSHI_API_KEY"),
    readProcessEnv(() => process.env.NEXT_PUBLIC_MUSHI_API_KEY),
    readProcessEnv(() => process.env.EXPO_PUBLIC_MUSHI_API_KEY),
    readProcessEnv(() => process.env.MUSHI_API_KEY)
  );
  const apiEndpoint = first(
    tryImportMetaEnv("VITE_MUSHI_API_ENDPOINT"),
    tryImportMetaEnv("NEXT_PUBLIC_MUSHI_API_ENDPOINT"),
    tryImportMetaEnv("NUXT_PUBLIC_MUSHI_API_ENDPOINT"),
    tryImportMetaEnv("EXPO_PUBLIC_MUSHI_API_ENDPOINT"),
    readProcessEnv(() => process.env.NEXT_PUBLIC_MUSHI_API_ENDPOINT),
    readProcessEnv(() => process.env.EXPO_PUBLIC_MUSHI_API_ENDPOINT),
    readProcessEnv(() => process.env.MUSHI_API_ENDPOINT)
  );
  const result = {};
  if (projectId) result.projectId = projectId;
  if (apiKey) result.apiKey = apiKey;
  if (apiEndpoint) result.apiEndpoint = apiEndpoint;
  return result;
}
var ENV_PREFIXES = ["VITE_", "NEXT_PUBLIC_", "NUXT_PUBLIC_", "EXPO_PUBLIC_", ""];
var FIELD_SUFFIX = {
  projectId: "MUSHI_PROJECT_ID",
  apiKey: "MUSHI_API_KEY"
};
function diagnoseEnvConfig() {
  const resolved = resolveEnvConfig();
  const missing = [];
  if (!resolved.projectId) missing.push("projectId");
  if (!resolved.apiKey) missing.push("apiKey");
  if (missing.length === 0) {
    return { resolved, missing, nearMisses: [], message: null };
  }
  const importMetaEnv = (() => {
    try {
      const env = import.meta.env;
      return typeof env === "object" && env !== null ? env : null;
    } catch {
      return null;
    }
  })();
  const processEnv = (() => {
    try {
      return typeof process !== "undefined" && process.env ? process.env : null;
    } catch {
      return null;
    }
  })();
  const nearMisses = [];
  for (const field of missing) {
    for (const prefix of ENV_PREFIXES) {
      const key = `${prefix}${FIELD_SUFFIX[field]}`;
      if (importMetaEnv && importMetaEnv[key]) {
        nearMisses.push({ field, key, via: "import.meta.env" });
      } else if (processEnv && processEnv[key]) {
        nearMisses.push({ field, key, via: "process.env" });
      }
    }
  }
  const expectedPrefix = importMetaEnv ? "VITE_MUSHI_*" : "NEXT_PUBLIC_MUSHI_* (Next.js), EXPO_PUBLIC_MUSHI_* (Expo), or MUSHI_* (Node / React Native)";
  const lines = [`[mushi] Missing ${missing.join(" and ")} \u2014 the SDK will not initialize.`];
  for (const m of nearMisses) {
    lines.push(
      `[mushi] Found ${m.key} in ${m.via}, but this runtime did not resolve it \u2014 it is either the wrong prefix for your bundler or not exposed to the client bundle.`
    );
  }
  lines.push(`[mushi] This runtime reads: ${expectedPrefix}. Set the matching variable(s) and restart your dev server.`);
  if (nearMisses.length === 0) {
    lines.push(
      `[mushi] If you already set the variable, check the prefix: a mis-prefixed var is stripped from client bundles and resolves to undefined with no error.`
    );
  }
  return { resolved, missing, nearMisses, message: lines.join("\n") };
}

// src/presets.ts
var KNOWN_PRESETS = [
  "production-calm",
  "beta-loud",
  "internal-debug",
  "manual-only",
  "minimal",
  "standard",
  "full"
];
var KNOWN_CONFIG_KEYS = [
  "projectId",
  "apiKey",
  "apiEndpoint",
  "tunnel",
  "timeout",
  "maxRetries",
  "circuitBreaker",
  "preset",
  "runtimeConfig",
  "analytics",
  "trackSessions",
  "sentry",
  "widget",
  "notifications",
  "capture",
  "privacy",
  "proactive",
  "preFilter",
  "integrations",
  "offline",
  "rewards",
  "assistant",
  "debug",
  "enabled",
  "appVersion",
  "sampleRate",
  "replaySampleRate",
  "replaysOnErrorSampleRate",
  "beforeSend",
  "beforeSendFeedback",
  "ignoreErrors",
  "denyUrls",
  "allowUrls",
  "onCrashedLastRun"
];
function presetDefaults(preset) {
  switch (preset) {
    case "manual-only":
      return {
        widget: { trigger: "manual", outdatedBanner: "console-only" },
        capture: { console: true, network: true, performance: false, screenshot: "on-report", elementSelector: false },
        proactive: { rageClick: false, longTask: false, apiCascade: false, errorBoundary: false }
      };
    case "beta-loud":
      return {
        widget: { trigger: "auto", outdatedBanner: "banner" },
        capture: { console: true, network: true, performance: true, screenshot: "auto", elementSelector: true },
        proactive: { rageClick: true, longTask: true, apiCascade: true, errorBoundary: true }
      };
    case "internal-debug":
      return {
        widget: { trigger: "auto", outdatedBanner: "banner", brandFooter: true },
        capture: { console: true, network: true, performance: true, screenshot: "auto", elementSelector: true },
        proactive: {
          rageClick: true,
          longTask: true,
          apiCascade: true,
          errorBoundary: true,
          cooldown: { maxProactivePerSession: 10, dismissCooldownHours: 0, suppressAfterDismissals: 99 }
        }
      };
    case "production-calm":
      return {
        widget: { trigger: "auto", outdatedBanner: "console-only" },
        capture: { console: true, network: true, performance: false, screenshot: "on-report", elementSelector: false },
        proactive: { rageClick: false, longTask: false, apiCascade: false, errorBoundary: false }
      };
    // Lean posture: widget on, console capture only, screenshot on-report,
    // no network/performance/replay, no proactive nudges.
    case "minimal":
      return {
        widget: { trigger: "auto" },
        capture: {
          console: true,
          network: false,
          performance: false,
          screenshot: "on-report",
          replay: "off",
          elementSelector: false
        },
        proactive: { rageClick: false, longTask: false, apiCascade: false, errorBoundary: false }
      };
    // Everything-on posture: full capture (incl. self-contained `lite` replay)
    // and every proactive trigger.
    case "full":
      return {
        widget: { trigger: "auto" },
        capture: {
          console: true,
          network: true,
          performance: true,
          screenshot: "auto",
          replay: "lite",
          elementSelector: true
        },
        proactive: { rageClick: true, longTask: true, apiCascade: true, errorBoundary: true }
      };
    // 'standard' and anything unrecognised → no expansion.
    default:
      return void 0;
  }
}
function expandPreset(config) {
  const defaults2 = presetDefaults(config.preset);
  if (!defaults2) return config;
  return {
    ...config,
    widget: {
      ...defaults2.widget,
      ...config.widget
    },
    capture: {
      ...defaults2.capture,
      ...config.capture
    },
    proactive: {
      ...defaults2.proactive,
      ...config.proactive,
      cooldown: {
        ...defaults2.proactive?.cooldown,
        ...config.proactive?.cooldown
      }
    }
  };
}
function isSilenced() {
  const truthy = (v) => v === "1" || v === "true";
  try {
    if (typeof process !== "undefined" && process.env && truthy(process.env.MUSHI_SILENT)) return true;
  } catch {
  }
  try {
    const env = import.meta.env;
    if (env && typeof env === "object" && (truthy(env.MUSHI_SILENT) || truthy(env.VITE_MUSHI_SILENT))) return true;
  } catch {
  }
  return false;
}
function validateConfig(config) {
  try {
    if (isSilenced()) return;
    if (!config || typeof config !== "object") return;
    const unknownKeys = Object.keys(config).filter((key) => !KNOWN_CONFIG_KEYS.includes(key));
    if (unknownKeys.length > 0) {
      console.error(
        `[mushi] Unknown config key${unknownKeys.length > 1 ? "s" : ""}: ${unknownKeys.map((k) => `\`${k}\``).join(", ")} \u2014 check for typos (see MushiConfig). Ignored.`
      );
    }
    if (config.preset !== void 0 && !KNOWN_PRESETS.includes(config.preset)) {
      console.error(
        `[mushi] Unknown preset \`${String(config.preset)}\` \u2014 expected one of ${KNOWN_PRESETS.map(
          (p) => `'${p}'`
        ).join(", ")}. Preset ignored.`
      );
    }
  } catch {
  }
}

// src/pre-filter.ts
var DEFAULT_MIN_LENGTH = 10;
var DEFAULT_MAX_LENGTH = 2e3;
var SPAM_PATTERNS = [
  /^(.)\1{10,}$/,
  // repeated single character
  /^[A-Z\s!?]{20,}$/,
  // all caps shouting
  /^[\d\s]+$/,
  // numbers only
  /^[^a-zA-Z\u00C0-\u024F\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF]{10,}$/,
  // no real letters
  // "test" removed — users legitimately write "I was testing…" in real reports.
  // Keep clearly-nonsense patterns that are never real sentences.
  /^(asdf|qwerty|lorem ipsum)[.,!?\s]*$/i
  // standalone keyboard-mash or placeholder
];
var GIBBERISH_PATTERN = /^[bcdfghjklmnpqrstvwxz]{6,}/i;
function createPreFilter(config = {}) {
  const {
    enabled = true,
    blockObviousSpam = true,
    minDescriptionLength = DEFAULT_MIN_LENGTH,
    maxDescriptionLength = DEFAULT_MAX_LENGTH
  } = config;
  function check(description) {
    if (!enabled) {
      return { passed: true };
    }
    const trimmed = description.trim();
    if (trimmed.length < minDescriptionLength) {
      return { passed: false, reason: `Too short (min ${minDescriptionLength} characters)` };
    }
    if (trimmed.length > maxDescriptionLength) {
      return { passed: false, reason: `Too long (max ${maxDescriptionLength} characters)` };
    }
    if (blockObviousSpam) {
      for (const pattern of SPAM_PATTERNS) {
        if (pattern.test(trimmed)) {
          return { passed: false, reason: "Detected as spam" };
        }
      }
      if (GIBBERISH_PATTERN.test(trimmed)) {
        return { passed: false, reason: "Detected as gibberish" };
      }
      const words = trimmed.split(/\s+/).filter((w) => w.length > 1);
      if (words.length < 2) {
        return { passed: false, reason: "Description needs at least 2 words" };
      }
    }
    return { passed: true };
  }
  function truncate(description) {
    const trimmed = description.trim();
    if (trimmed.length <= maxDescriptionLength) return trimmed;
    return trimmed.slice(0, maxDescriptionLength) + "...";
  }
  return { check, truncate };
}

// src/logger.ts
var LEVEL_VALUE = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  fatal: 50,
  silent: 99
};
var LEVEL_LABEL = {
  debug: "DBG",
  info: "INF",
  warn: "WRN",
  error: "ERR",
  fatal: "FTL"
};
var ANSI = {
  reset: "\x1B[0m",
  dim: "\x1B[2m",
  bold: "\x1B[1m",
  cyan: "\x1B[36m",
  green: "\x1B[32m",
  yellow: "\x1B[33m",
  red: "\x1B[31m",
  white: "\x1B[37m",
  bgRed: "\x1B[41m"
};
var LEVEL_COLOR = {
  debug: ANSI.dim,
  info: ANSI.green,
  warn: ANSI.yellow,
  error: ANSI.red,
  fatal: `${ANSI.bgRed}${ANSI.white}${ANSI.bold}`
};
function detectFormat() {
  try {
    if (typeof globalThis.Deno !== "undefined") return "json";
  } catch {
  }
  const proc = typeof globalThis.process !== "undefined" ? globalThis.process : void 0;
  if (proc?.env) {
    if (proc.env.NODE_ENV === "production") return "json";
    if (proc.env.LOG_FORMAT === "json") return "json";
    if (proc.env.LOG_FORMAT === "pretty") return "pretty";
    if (proc.stdout?.isTTY) return "pretty";
  }
  if (typeof globalThis.window !== "undefined") return "pretty";
  return "json";
}
function flattenMeta(meta) {
  const parts = [];
  for (const [k, v] of Object.entries(meta)) {
    if (v === void 0 || v === null) continue;
    if (typeof v === "object") {
      parts.push(`${k}=${JSON.stringify(v)}`);
    } else {
      parts.push(`${k}=${String(v)}`);
    }
  }
  return parts.join(" ");
}
function formatPretty(entry) {
  const { ts, level, scope, msg, ...rest } = entry;
  const time = ts.slice(11, 23);
  const color = LEVEL_COLOR[level] ?? "";
  const label = LEVEL_LABEL[level] ?? level.toUpperCase();
  const metaStr = Object.keys(rest).length > 0 ? ` ${ANSI.dim}${flattenMeta(rest)}${ANSI.reset}` : "";
  return `${ANSI.dim}${time}${ANSI.reset} ${color}${label}${ANSI.reset} ${ANSI.cyan}[${scope}]${ANSI.reset} ${msg}${metaStr}`;
}
function formatJson(entry) {
  return JSON.stringify(entry);
}
function makeEmitter(destination) {
  if (destination === "stderr") {
    return (level, formatted) => {
      console.error(formatted);
    };
  }
  return (level, formatted) => {
    switch (level) {
      case "error":
      case "fatal":
        console.error(formatted);
        break;
      case "warn":
        console.warn(formatted);
        break;
      default:
        console.log(formatted);
    }
  };
}
function buildLogger(scope, minLevel, baseMeta, formatter, emit) {
  let currentLevel = minLevel;
  function log(level, msg, meta) {
    if (LEVEL_VALUE[level] < LEVEL_VALUE[currentLevel]) return;
    const entry = {
      ts: (/* @__PURE__ */ new Date()).toISOString(),
      level,
      scope,
      msg,
      ...baseMeta,
      ...meta
    };
    emit(level, formatter(entry));
  }
  return {
    debug: (msg, meta) => log("debug", msg, meta),
    info: (msg, meta) => log("info", msg, meta),
    warn: (msg, meta) => log("warn", msg, meta),
    error: (msg, meta) => log("error", msg, meta),
    fatal: (msg, meta) => log("fatal", msg, meta),
    child(childScope, childMeta) {
      return buildLogger(
        `${scope}:${childScope}`,
        currentLevel,
        { ...baseMeta, ...childMeta },
        formatter,
        emit
      );
    },
    setLevel(level) {
      currentLevel = level;
    }
  };
}
function createLogger(options) {
  const {
    scope,
    level = "info",
    meta = {},
    format = "auto",
    destination = "stdout"
  } = options;
  const resolvedFormat = format === "auto" ? detectFormat() : format;
  const formatter = resolvedFormat === "json" ? formatJson : formatPretty;
  const emit = makeEmitter(destination);
  return buildLogger(scope, level, meta, formatter, emit);
}
var noopLogger = {
  debug: () => {
  },
  info: () => {
  },
  warn: () => {
  },
  error: () => {
  },
  fatal: () => {
  },
  child: () => noopLogger,
  setLevel: () => {
  }
};

// src/queue-crypto.ts
var KEY_DB = "mushi-mushi-keyring";
var KEY_STORE = "keys";
var KEY_RECORD_ID = "offline-queue/v1";
var cachedKey = null;
var cachedKeyPromise = null;
function hasWebCrypto() {
  return typeof globalThis !== "undefined" && typeof globalThis.crypto !== "undefined" && typeof globalThis.crypto.subtle !== "undefined" && typeof indexedDB !== "undefined";
}
function openKeyDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(KEY_DB, 1);
    req.onupgradeneeded = () => {
      try {
        const db = req.result;
        if (!db) return;
        if (!db.objectStoreNames.contains(KEY_STORE)) {
          db.createObjectStore(KEY_STORE);
        }
      } catch {
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      if (!db || !db.objectStoreNames.contains(KEY_STORE)) {
        try {
          db?.close();
        } catch {
        }
        reject(new Error("key object store unavailable"));
        return;
      }
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}
async function loadKey() {
  const db = await openKeyDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, "readonly");
    const req = tx.objectStore(KEY_STORE).get(KEY_RECORD_ID);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}
async function storeKey(key) {
  const db = await openKeyDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, "readwrite");
    tx.objectStore(KEY_STORE).put(key, KEY_RECORD_ID);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function getOfflineQueueKey() {
  if (cachedKey) return cachedKey;
  if (cachedKeyPromise) return cachedKeyPromise;
  if (!hasWebCrypto()) {
    throw new Error("Web Crypto + IndexedDB required for offline queue encryption");
  }
  cachedKeyPromise = (async () => {
    const existing = await loadKey();
    if (existing) {
      cachedKey = existing;
      return existing;
    }
    const key = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );
    await storeKey(key);
    cachedKey = key;
    return key;
  })();
  return cachedKeyPromise;
}
function bytesToB64(bytes) {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
function b64ToBytes(s) {
  const bin = atob(s);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
async function encryptJson(plain) {
  const key = await getOfflineQueueKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(plain));
  const cipher = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data));
  return { _mme: 1, iv: bytesToB64(iv), ct: bytesToB64(cipher) };
}
function isEncryptedPayload(v) {
  return !!v && typeof v === "object" && v._mme === 1 && typeof v.iv === "string" && typeof v.ct === "string";
}
async function decryptJson(payload) {
  const key = await getOfflineQueueKey();
  const iv = b64ToBytes(payload.iv);
  const ct = b64ToBytes(payload.ct);
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(plain));
}

// src/queue.ts
var queueLog = createLogger({ scope: "mushi:queue", level: "warn" });
var DB_NAME = "mushi-mushi";
var STORE_NAME = "offline-reports";
var DB_VERSION = 1;
var LS_KEY = "mushi_offline_queue";
var BATCH_SIZE = 10;
var MAX_BACKOFF_MS = 6e4;
var AUTO_FLUSH_INTERVAL_MS = 3e4;
var MAX_DELIVERY_ATTEMPTS = 8;
var MAX_QUEUE_AGE_MS = 24 * 60 * 60 * 1e3;
function createOfflineQueue(config = {}) {
  const { enabled = true, maxQueueSize = 50, syncOnReconnect = true, encryptAtRest = true } = config;
  let syncCleanup = null;
  let flushInterval = null;
  let backendType = null;
  let inFlight = null;
  let followUp = null;
  async function wrapForStorage(report) {
    const queuedAt = (/* @__PURE__ */ new Date()).toISOString();
    if (!encryptAtRest) {
      return { ...report, queuedAt };
    }
    try {
      const payload = await encryptJson(report);
      return { id: report.id, queuedAt, payload };
    } catch (err) {
      queueLog.warn("Offline queue: encryption failed, storing plaintext", { err: String(err) });
      return { ...report, queuedAt };
    }
  }
  async function unwrapForSend(row) {
    if (isEncryptedRecord(row)) {
      try {
        return await decryptJson(row.payload);
      } catch (err) {
        queueLog.warn("Offline queue: decrypt failed, dropping row", { err: String(err), id: row.id });
        return null;
      }
    }
    return row;
  }
  function isEncryptedRecord(row) {
    return !!row.payload && isEncryptedPayload(row.payload);
  }
  function rowAttempts(row) {
    const n = row.attempts;
    return typeof n === "number" && Number.isFinite(n) ? n : 0;
  }
  function isExpired(row, now3) {
    const queuedAt = row.queuedAt;
    if (!queuedAt) return false;
    const ts = Date.parse(queuedAt);
    if (Number.isNaN(ts)) return false;
    return now3 - ts > MAX_QUEUE_AGE_MS;
  }
  function detectBackend() {
    if (backendType) return backendType;
    if (typeof indexedDB !== "undefined") {
      backendType = "indexeddb";
    } else if (typeof localStorage !== "undefined") {
      backendType = "localstorage";
    } else {
      backendType = "none";
    }
    return backendType;
  }
  function openDb() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        try {
          const db = request.result;
          if (!db) return;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: "id" });
          }
        } catch {
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        if (!db || !db.objectStoreNames.contains(STORE_NAME)) {
          backendType = "localstorage";
          try {
            db?.close();
          } catch {
          }
          reject(new Error("queue object store unavailable"));
          return;
        }
        resolve(db);
      };
      request.onerror = () => {
        backendType = "localstorage";
        reject(request.error);
      };
    });
  }
  async function idbEnqueue(report) {
    const db = await openDb();
    const row = await wrapForStorage(report);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(row);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  async function idbGetAll() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  async function idbDelete(id) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  async function idbPutRow(row) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(row);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  async function idbSize() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  async function idbClear() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  function lsRead() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }
  function lsWrite(rows) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(rows));
    } catch {
    }
  }
  async function lsEnqueue(report) {
    const rows = lsRead();
    const row = await wrapForStorage(report);
    const idx = rows.findIndex((r) => r.id === report.id);
    if (idx >= 0) rows[idx] = row;
    else rows.push(row);
    lsWrite(rows);
  }
  function lsDelete(id) {
    const rows = lsRead().filter((r) => r.id !== id);
    lsWrite(rows);
  }
  function lsUpdateRow(row) {
    const rows = lsRead();
    const idx = rows.findIndex((r) => r.id === row.id);
    if (idx >= 0) {
      rows[idx] = row;
      lsWrite(rows);
    }
  }
  async function removeRow(backend, rowId) {
    try {
      if (backend === "indexeddb") await idbDelete(rowId);
      else lsDelete(rowId);
    } catch (err) {
      queueLog.debug("Offline queue: row removal failed (will age out)", {
        id: rowId,
        err: String(err)
      });
    }
  }
  async function persistRow(backend, row) {
    try {
      if (backend === "indexeddb") await idbPutRow(row);
      else lsUpdateRow(row);
      return true;
    } catch (err) {
      queueLog.debug("Offline queue: row persist failed", {
        id: row.id,
        err: String(err)
      });
      return false;
    }
  }
  async function readRows() {
    const backend = detectBackend();
    if (backend === "none") return { backend, rows: [] };
    if (backend === "indexeddb") {
      try {
        return { backend, rows: await idbGetAll() };
      } catch {
        backendType = "localstorage";
      }
    }
    return { backend: "localstorage", rows: lsRead() };
  }
  async function writeRow(report) {
    const backend = detectBackend();
    if (backend === "none") {
      queueLog.debug("Offline queue: no storage backend, dropping report", { id: report.id });
      return backend;
    }
    if (backend === "indexeddb") {
      try {
        await idbEnqueue(report);
        return backend;
      } catch {
        backendType = "localstorage";
      }
    }
    await lsEnqueue(report);
    return "localstorage";
  }
  async function enqueue2(report) {
    if (!enabled) return;
    const currentSize = await size();
    if (currentSize >= maxQueueSize) {
      queueLog.warn("Offline queue full \u2014 dropping report", { maxQueueSize });
      return;
    }
    await writeRow(report);
  }
  function getBackoffDelay2(attempt) {
    return Math.min(1e3 * 2 ** attempt + Math.random() * 500, MAX_BACKOFF_MS);
  }
  function sleep2(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  async function runFlush(client) {
    const { backend, rows: storedRows } = await readRows();
    let rows = storedRows;
    const now3 = Date.now();
    const fresh = [];
    for (const row of rows) {
      if (isExpired(row, now3)) {
        const rowId = row.id;
        await removeRow(backend, rowId);
        queueLog.debug("Offline queue: evicting stale report", { id: rowId });
      } else {
        fresh.push(row);
      }
    }
    rows = fresh;
    const batch = rows.slice(0, BATCH_SIZE);
    let sent = 0;
    let failed = 0;
    for (let i = 0; i < batch.length; i++) {
      const row = batch[i];
      const rowId = row.id;
      const report = await unwrapForSend(row);
      if (!report) {
        await removeRow(backend, rowId);
        failed++;
        continue;
      }
      const result = await client.submitReport(report);
      if (result.ok) {
        await removeRow(backend, rowId);
        sent++;
        continue;
      }
      failed++;
      if (result.error?.code === "CIRCUIT_OPEN") {
        queueLog.debug("Offline queue: circuit open, deferring the rest of the batch", {
          id: rowId,
          remaining: batch.length - i
        });
        break;
      }
      const permanent = result.error?.code === "HTTP_400" || result.error?.code === "HTTP_413" || result.error?.code === "HTTP_422" || result.error?.code === "INGEST_ERROR" || result.error?.code === "VALIDATION_ERROR" || // A payload that exceeds the size guard will never shrink on its own;
      // retrying re-serialises the multi-MB body every sync tick and wedges
      // the queue (it matches neither permanent nor transient otherwise).
      // SERIALIZE_FAILED (circular ref) is likewise unrecoverable on retry.
      result.error?.code === "PAYLOAD_TOO_LARGE" || result.error?.code === "SERIALIZE_FAILED" || typeof result.error?.message === "string" && /invalid payload|description must be at least|validation/i.test(
        result.error.message
      );
      const transient = !permanent && (result.error?.code === "NETWORK_ERROR" || result.error?.code === "HTTP_403" || result.error?.code === "HTTP_429" || result.error?.code === "HTTP_502" || result.error?.code === "HTTP_503" || result.error?.code === "HTTP_504" || typeof result.error?.code === "string" && result.error.code.startsWith("HTTP_5"));
      if (permanent) {
        await removeRow(backend, rowId);
      } else {
        const nextAttempts = rowAttempts(row) + 1;
        if (nextAttempts >= MAX_DELIVERY_ATTEMPTS) {
          await removeRow(backend, rowId);
          queueLog.warn("Offline queue: giving up on report after repeated failures", {
            id: rowId,
            attempts: nextAttempts,
            code: result.error?.code
          });
        } else {
          row.attempts = nextAttempts;
          const persisted = await persistRow(backend, row);
          if (persisted) {
            queueLog.debug("Offline queue: delivery failed, will retry", {
              id: rowId,
              attempts: nextAttempts,
              code: result.error?.code,
              classified: transient ? "transient" : "unclassified"
            });
          } else {
            await removeRow(backend, rowId);
            queueLog.warn("Offline queue: dropping report (attempt counter unpersistable)", {
              id: rowId,
              code: result.error?.code
            });
          }
        }
      }
      if (i < batch.length - 1) {
        await sleep2(getBackoffDelay2(i));
      }
    }
    return { sent, failed };
  }
  function flush(client) {
    if (!enabled) return Promise.resolve({ sent: 0, failed: 0 });
    if (inFlight) {
      if (!followUp) {
        followUp = inFlight.catch(() => void 0).then(() => {
          followUp = null;
          return flush(client);
        });
      }
      return followUp;
    }
    const pass = runFlush(client).finally(() => {
      if (inFlight === pass) inFlight = null;
    });
    inFlight = pass;
    return pass;
  }
  async function size() {
    const backend = detectBackend();
    if (backend === "indexeddb") {
      try {
        return await idbSize();
      } catch {
        backendType = "localstorage";
      }
    }
    return lsRead().length;
  }
  async function clear() {
    const backend = detectBackend();
    if (backend === "indexeddb") {
      try {
        await idbClear();
      } catch {
      }
    }
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
    }
  }
  function startAutoSync(client) {
    if (!enabled || !syncOnReconnect || typeof window === "undefined") return;
    if (syncCleanup) stopAutoSync();
    const tryFlush = () => {
      if (navigator.onLine) {
        flush(client).catch((err) => {
          queueLog.warn("Offline queue auto-flush failed", { err: String(err) });
        });
      }
    };
    window.addEventListener("online", tryFlush);
    flushInterval = setInterval(() => {
      void size().then((n) => {
        if (n > 0) tryFlush();
      });
    }, AUTO_FLUSH_INTERVAL_MS);
    syncCleanup = () => {
      window.removeEventListener("online", tryFlush);
      if (flushInterval) {
        clearInterval(flushInterval);
        flushInterval = null;
      }
    };
  }
  function stopAutoSync() {
    syncCleanup?.();
    syncCleanup = null;
  }
  return { enqueue: enqueue2, flush, size, clear, startAutoSync, stopAutoSync };
}

// src/environment.ts
function captureEnvironment() {
  const nav = typeof navigator !== "undefined" ? navigator : void 0;
  const win = typeof window !== "undefined" ? window : void 0;
  const doc = typeof document !== "undefined" ? document : void 0;
  const scr = typeof screen !== "undefined" ? screen : void 0;
  void kickOffUserAgentData(nav);
  const connection = nav && "connection" in nav ? nav.connection : void 0;
  const w = win;
  const isCapacitor = !!w?.Capacitor;
  const isCordova = !!w?.cordova;
  const isReactNative = nav?.product === "ReactNative" || typeof globalThis.HermesInternal !== "undefined";
  const native = isCapacitor || isCordova || isReactNative ? {
    ...isCapacitor ? { capacitor: true } : {},
    ...isCordova ? { cordova: true } : {},
    ...isReactNative ? { reactNative: true } : {}
  } : void 0;
  return {
    userAgent: nav?.userAgent ?? "unknown",
    platform: nav?.platform ?? "unknown",
    language: nav?.language ?? "en",
    viewport: {
      width: win?.innerWidth ?? 0,
      height: win?.innerHeight ?? 0
    },
    url: win?.location?.href ?? "",
    referrer: doc?.referrer ?? "",
    timestamp: (/* @__PURE__ */ new Date()).toISOString(),
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone ?? "UTC",
    connection: connection ? {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt
    } : void 0,
    deviceMemory: nav?.deviceMemory,
    hardwareConcurrency: nav?.hardwareConcurrency,
    route: win?.location?.pathname,
    nearestTestid: findNearestTestidFromActive(doc),
    userAgentData: captureUserAgentData(nav),
    screen: captureScreen(scr, win),
    prefersColorScheme: matchScheme(win),
    prefersReducedMotion: matchMedia(win, "(prefers-reduced-motion: reduce)"),
    prefersReducedData: matchMedia(win, "(prefers-reduced-data: reduce)"),
    prefersContrast: matchContrast(win),
    forcedColors: matchMedia(win, "(forced-colors: active)"),
    online: typeof nav?.onLine === "boolean" ? nav.onLine : void 0,
    displayMode: matchDisplayMode(win),
    documentTitle: doc?.title?.slice(0, 200),
    buildId: readBuildIdMeta(doc),
    pageLoadTiming: capturePageLoadTiming(win),
    ...native ? { native } : {}
  };
}
function findNearestTestidFromActive(doc) {
  if (!doc) return void 0;
  let cur = doc.activeElement ?? null;
  let hops = 0;
  while (cur && hops < 20) {
    const tid = cur.getAttribute?.("data-testid");
    if (tid) return tid;
    cur = cur.parentElement;
    hops++;
  }
  return void 0;
}
var cachedHighEntropy = null;
var highEntropyKickedOff = false;
function kickOffUserAgentData(nav) {
  if (highEntropyKickedOff) return;
  const ua = nav?.userAgentData;
  if (!ua?.getHighEntropyValues) return;
  highEntropyKickedOff = true;
  ua.getHighEntropyValues([
    "architecture",
    "bitness",
    "model",
    "platformVersion",
    "uaFullVersion",
    "fullVersionList"
  ]).then((v) => {
    cachedHighEntropy = v;
  }).catch(() => {
  });
}
function pickBrand(brands) {
  if (!brands?.length) return void 0;
  const real = brands.filter((b) => !/not.?a.?brand/i.test(b.brand));
  if (real.length === 0) return void 0;
  const named = real.find((b) => !/chromium|google chrome/i.test(b.brand));
  return named ?? real[0];
}
function captureUserAgentData(nav) {
  const low = nav?.userAgentData;
  if (!low && !cachedHighEntropy) return void 0;
  const fullList = cachedHighEntropy?.fullVersionList;
  const brand = pickBrand(fullList ?? low?.brands);
  const out = {};
  if (brand) {
    out.browser = brand.brand;
    out.browserVersion = brand.version;
  }
  if (low?.platform) out.os = low.platform;
  if (cachedHighEntropy?.platformVersion) out.osVersion = cachedHighEntropy.platformVersion;
  if (typeof low?.mobile === "boolean") out.mobile = low.mobile;
  if (cachedHighEntropy?.model) out.model = cachedHighEntropy.model;
  if (cachedHighEntropy?.architecture) out.architecture = cachedHighEntropy.architecture;
  if (cachedHighEntropy?.bitness) out.bitness = cachedHighEntropy.bitness;
  return Object.keys(out).length === 0 ? void 0 : out;
}
function captureScreen(scr, win) {
  if (!scr && !win) return void 0;
  const out = {};
  if (typeof scr?.width === "number") out.width = scr.width;
  if (typeof scr?.height === "number") out.height = scr.height;
  if (typeof win?.devicePixelRatio === "number") out.devicePixelRatio = win.devicePixelRatio;
  if (typeof scr?.colorDepth === "number") out.colorDepth = scr.colorDepth;
  const orientationType = scr?.orientation?.type;
  if (orientationType) out.orientation = orientationType;
  return Object.keys(out).length === 0 ? void 0 : out;
}
function matchMedia(win, query) {
  if (!win?.matchMedia) return void 0;
  try {
    return win.matchMedia(query).matches;
  } catch {
    return void 0;
  }
}
function matchScheme(win) {
  if (!win?.matchMedia) return void 0;
  if (matchMedia(win, "(prefers-color-scheme: dark)")) return "dark";
  if (matchMedia(win, "(prefers-color-scheme: light)")) return "light";
  return "no-preference";
}
function matchContrast(win) {
  if (!win?.matchMedia) return void 0;
  if (matchMedia(win, "(prefers-contrast: more)")) return "more";
  if (matchMedia(win, "(prefers-contrast: less)")) return "less";
  if (matchMedia(win, "(prefers-contrast: custom)")) return "custom";
  return "no-preference";
}
function matchDisplayMode(win) {
  if (!win?.matchMedia) return void 0;
  if (matchMedia(win, "(display-mode: fullscreen)")) return "fullscreen";
  if (matchMedia(win, "(display-mode: standalone)")) return "standalone";
  if (matchMedia(win, "(display-mode: minimal-ui)")) return "minimal-ui";
  if (matchMedia(win, "(display-mode: browser)")) return "browser";
  return void 0;
}
function readBuildIdMeta(doc) {
  if (!doc) return void 0;
  const el = doc.querySelector?.('meta[name="mushi:build"]');
  const v = el?.content?.trim();
  if (!v) return void 0;
  return v.slice(0, 64);
}
function capturePageLoadTiming(win) {
  const perf = win?.performance;
  if (!perf?.getEntriesByType) return void 0;
  let entry;
  try {
    const entries = perf.getEntriesByType("navigation");
    entry = entries[0];
  } catch {
    return void 0;
  }
  if (!entry) return void 0;
  const start = entry.startTime ?? 0;
  const out = {};
  if (entry.domContentLoadedEventEnd > 0)
    out.domContentLoadedMs = Math.round(entry.domContentLoadedEventEnd - start);
  if (entry.loadEventEnd > 0) out.loadCompleteMs = Math.round(entry.loadEventEnd - start);
  if (entry.responseStart > 0) out.timeToFirstByteMs = Math.round(entry.responseStart - start);
  if (typeof entry.type === "string") out.navigationType = entry.type;
  return Object.keys(out).length === 0 ? void 0 : out;
}

// src/reporter-token.ts
var LEGACY_STORAGE_KEY = "mushi_reporter_token";
function storageKey(projectId) {
  return `mushi:reporter-token:${projectId}`;
}
function generateToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `mushi_${crypto.randomUUID()}`;
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `mushi_${hex}`;
}
function readStoredToken(key) {
  if (typeof localStorage === "undefined") return null;
  try {
    const existing = localStorage.getItem(key);
    return existing && existing.length > 0 ? existing : null;
  } catch {
    return null;
  }
}
function writeStoredToken(key, token) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key, token);
  } catch {
  }
}
function getReporterToken(projectId) {
  if (!projectId) {
    const legacy2 = readStoredToken(LEGACY_STORAGE_KEY);
    if (legacy2) return legacy2;
    const token2 = generateToken();
    writeStoredToken(LEGACY_STORAGE_KEY, token2);
    return token2;
  }
  const key = storageKey(projectId);
  const scoped = readStoredToken(key);
  if (scoped) return scoped;
  const legacy = readStoredToken(LEGACY_STORAGE_KEY);
  const token = legacy ?? generateToken();
  writeStoredToken(key, token);
  return token;
}

// src/uuid.ts
function newUuid() {
  const c = typeof crypto !== "undefined" ? crypto : void 0;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = bytes[6] & 15 | 64;
  bytes[8] = bytes[8] & 63 | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

// src/fingerprint.ts
var CACHE_KEY = "mushi_fingerprint_hash";
function collectInputs() {
  const nav = typeof navigator !== "undefined" ? navigator : void 0;
  const scr = typeof screen !== "undefined" ? screen : void 0;
  const win = typeof window !== "undefined" ? window : void 0;
  return {
    userAgent: nav?.userAgent ?? "unknown",
    platform: nav?.platform ?? "unknown",
    language: nav?.language ?? "en",
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone ?? "UTC",
    screenWidth: scr?.width ?? 0,
    screenHeight: scr?.height ?? 0,
    pixelRatio: win?.devicePixelRatio ?? 1,
    deviceMemory: nav?.deviceMemory,
    hardwareConcurrency: nav?.hardwareConcurrency
  };
}
async function sha256Hex2(input) {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const buf = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return `fbk_${(hash >>> 0).toString(16).padStart(8, "0")}`;
}
async function getDeviceFingerprintHash() {
  if (typeof localStorage !== "undefined") {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return cached;
  }
  const inputs = collectInputs();
  const serialised = JSON.stringify(inputs);
  const hash = await sha256Hex2(serialised);
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(CACHE_KEY, hash);
    } catch {
    }
  }
  return hash;
}

// src/session.ts
var SESSION_KEY = "mushi_session_id";
var cachedSessionId = null;
function getSessionId() {
  if (cachedSessionId) return cachedSessionId;
  if (typeof sessionStorage !== "undefined") {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      cachedSessionId = existing;
      return existing;
    }
  }
  const id = generateSessionId();
  cachedSessionId = id;
  if (typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.setItem(SESSION_KEY, id);
    } catch {
    }
  }
  return id;
}
function generateSessionId() {
  const timestamp = Date.now().toString(36);
  return `ms_${timestamp}_${randomSuffix()}`;
}
function randomSuffix() {
  const c = typeof crypto !== "undefined" ? crypto : void 0;
  const bytes = new Uint8Array(4);
  if (c?.getRandomValues) {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let n = 0;
  for (const b of bytes) n = n * 256 + b >>> 0;
  return n.toString(36).padStart(6, "0").slice(0, 6);
}

// src/analytics-gate.ts
var AUTOMATED_UA_RE = /HeadlessChrome|PhantomJS|Lighthouse|\bbot\b|bot[/-]|crawler|spider/i;
function consentKey(projectId) {
  return "mushi_events_consent_" + projectId;
}
var listeners = /* @__PURE__ */ new Set();
function dntActive() {
  if (typeof navigator === "undefined") return false;
  const nav = navigator;
  if (nav.globalPrivacyControl === true) return true;
  const winDnt = typeof window !== "undefined" ? window.doNotTrack : void 0;
  const dnt = nav.doNotTrack ?? nav.msDoNotTrack ?? winDnt;
  return dnt === "1" || dnt === "yes";
}
function isAutomatedBrowser() {
  if (typeof navigator === "undefined") return false;
  if (navigator.webdriver === true) return true;
  return AUTOMATED_UA_RE.test(navigator.userAgent ?? "");
}
function analyticsBlockReason(config) {
  if (config?.enabled === false) return "disabled";
  if (config?.respectDoNotTrack !== false && dntActive()) return "dnt";
  if (config?.excludeBots !== false && isAutomatedBrowser()) return "automation";
  return null;
}
function readStoredConsent(projectId) {
  if (!projectId) return null;
  try {
    const v = localStorage.getItem(consentKey(projectId));
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}
function writeStoredConsent(projectId, state) {
  if (!projectId) return;
  try {
    localStorage.setItem(consentKey(projectId), state);
  } catch {
  }
}
function resolveAnalyticsConsent(projectId, config) {
  const stored = readStoredConsent(projectId);
  if (config?.consent === "required") return stored ?? "pending";
  return stored === "denied" ? "denied" : "granted";
}
function setAnalyticsConsent(projectId, state) {
  writeStoredConsent(projectId, state);
  for (const listener of [...listeners]) {
    try {
      listener(state);
    } catch {
    }
  }
}
function onAnalyticsConsentChange(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// src/session-tracker.ts
var HEARTBEAT_INTERVAL_MS = 6e4;
var _client = null;
var _sdkVersion;
var _userIdHash = null;
var _reporterToken = null;
var _pageViewCount = 0;
var _heartbeatTimer = null;
var _initialized = false;
var _active = false;
var _started = false;
var _unsubscribeConsent = null;
function now() {
  return (/* @__PURE__ */ new Date()).toISOString();
}
function currentRoute() {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
}
function referrerOrigin() {
  if (typeof document === "undefined" || !document.referrer) return null;
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}
function userAgent() {
  if (typeof navigator === "undefined") return null;
  return navigator.userAgent ?? null;
}
function buildPayload(kind, extra) {
  return {
    kind,
    session_id: getSessionId(),
    ts: now(),
    page_view_count: _pageViewCount,
    // Wire name kept for older servers; the value is the raw token (hashed server-side).
    reporter_token_hash: _reporterToken,
    user_id_hash: _userIdHash,
    user_agent: userAgent(),
    sdk_version: _sdkVersion,
    ...extra
  };
}
function send(payload) {
  if (!_client || !_active) return;
  _client.postSessionEvent(payload).catch(() => {
  });
}
function recordPageView(route) {
  if (!_active) return;
  _pageViewCount += 1;
  send(buildPayload("page_view", { route }));
}
function activate() {
  if (_active || !_initialized) return;
  _active = true;
  if (!_started) {
    _started = true;
    _pageViewCount = 1;
    send(buildPayload("session_start", { route: currentRoute(), referrer: referrerOrigin() }));
  }
  _heartbeatTimer = setInterval(() => {
    send(buildPayload("session_heartbeat", { route: currentRoute() }));
  }, HEARTBEAT_INTERVAL_MS);
}
function deactivate() {
  _active = false;
  if (_heartbeatTimer != null) {
    clearInterval(_heartbeatTimer);
    _heartbeatTimer = null;
  }
}
function initSessionTracker(opts) {
  if (_initialized || typeof window === "undefined") return;
  if (analyticsBlockReason(opts.analytics) !== null) return;
  _initialized = true;
  _client = opts.client;
  _sdkVersion = opts.sdkVersion;
  _reporterToken = opts.reporterToken ?? opts.reporterTokenHash ?? null;
  _userIdHash = opts.userIdHash ?? null;
  const onHide = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      send(buildPayload("session_end", { route: currentRoute() }));
    }
  };
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onHide, { passive: true });
  }
  window.addEventListener("pagehide", () => {
    send(buildPayload("session_end", { route: currentRoute() }));
  }, { passive: true });
  if (opts.patchHistory !== false) {
    patchHistoryForPageViews();
  }
  _unsubscribeConsent = onAnalyticsConsentChange((state) => {
    if (state === "granted") activate();
    else deactivate();
  });
  if (resolveAnalyticsConsent(opts.projectId ?? "", opts.analytics) === "granted") activate();
}
function trackPageView(route) {
  if (!_initialized || !_client) return;
  recordPageView(route ?? currentRoute());
}
function updateSessionIdentity(userIdHash) {
  _userIdHash = userIdHash;
}
function destroySessionTracker() {
  deactivate();
  _unsubscribeConsent?.();
  _unsubscribeConsent = null;
  _initialized = false;
  _started = false;
  _client = null;
}
var _historyPatched = false;
function patchHistoryForPageViews() {
  if (_historyPatched || typeof history === "undefined") return;
  _historyPatched = true;
  const wrap = (original, countsAsPageView) => function(...args) {
    const result = original.apply(this, args);
    if (countsAsPageView) recordPageView(currentRoute());
    return result;
  };
  history.pushState = wrap(history.pushState, true);
  history.replaceState = wrap(history.replaceState, false);
  window.addEventListener("popstate", () => {
    recordPageView(currentRoute());
  }, { passive: true });
}

// src/analytics-taxonomy.ts
var EVENT_NAME_RE = /^[a-z][a-z0-9_]{1,63}$/;
var EVENT_PROPERTY_LIMITS = {
  maxKeys: 40,
  maxKeyLength: 64,
  maxValueLength: 256,
  maxBytes: 8192,
  /** Client batch size before an immediate flush. */
  maxClientBatch: 20,
  /** Server-side per-request ceiling. */
  maxServerBatch: 50,
  /** Distinct event names per project before the server returns 422. */
  maxEventNamesPerProject: 200
};
var RESERVED_PROPERTY_PREFIX = "$";
var PII_PROPERTY_KEY_RE = /(email|phone|password|passwd|token|secret|ssn|address|credit|card|iban)/i;
var MUSHI_SURFACES = ["web", "console", "docs", "cli", "mcp", "server", "mobile"];
var MUSHI_EVENTS = {
  // ── Any surface (SDK `analytics.autoPageviews`) ──────────────────────────
  page_view: { surface: ["web", "docs", "console"], required: [] },
  // ── Landing + docs (apps/docs) ───────────────────────────────────────────
  // Every docs route; `route` repeats the reserved `$route` as a host prop so
  // the requirement is checkable. company_funnel_weekly counts visits from
  // this and landing_view.
  docs_page_view: { surface: "docs", required: ["route"] },
  landing_view: { surface: "docs", required: [] },
  cta_click: { surface: "docs", required: ["cta_id", "location"] },
  quickstart_view: { surface: "docs", required: [] },
  pricing_view: { surface: "docs", required: [] },
  connect_demo_click: { surface: "docs", required: ["href"] },
  signup_click: { surface: "docs", required: ["href"] },
  // ── Console (apps/admin) ─────────────────────────────────────────────────
  signup_completed: { surface: "console", required: ["signup_source"] },
  report_opened: { surface: ["console", "mcp"], required: ["report_id"] },
  fix_context_pulled: { surface: ["console", "mcp"], required: ["report_id"] },
  fix_dispatched: { surface: ["console", "mcp"], required: ["report_id", "agent"] },
  invite_sent: { surface: "console", required: [] },
  upgrade_clicked: { surface: "console", required: ["plan"] },
  test_report_sent: { surface: "console", required: ["project_id"] },
  diagnosis_viewed: { surface: "console", required: ["report_id", "project_id", "surface"] },
  // ── Server-emitted (edge functions) ──────────────────────────────────────
  project_created: { surface: "server", required: ["project_id"] },
  key_minted: { surface: "server", required: ["project_id"] },
  first_report_received: { surface: "server", required: ["project_id"] },
  first_diagnosis_ready: { surface: "server", required: ["project_id"] },
  fix_merged: { surface: "server", required: ["project_id"] },
  upgrade_completed: { surface: "server", required: ["project_id", "plan"] },
  repo_digest_created: { surface: "server", required: ["project_id"] },
  repo_diagram_generated: { surface: "server", required: ["project_id"] },
  repo_diagram_published: { surface: "server", required: ["project_id"] },
  public_diagram_viewed: { surface: "server", required: ["project_id"] },
  // ── Growth loop (widget "Bug reports by Mushi" mark) ─────────────────────
  loop_impression: { surface: "web", required: [] },
  loop_click: { surface: "web", required: [] },
  loop_signup: { surface: "console", required: ["ref"] }
};
var MUSHI_EVENT_NAMES = Object.keys(MUSHI_EVENTS);
var ACTIVATION_EVENT = "first_report_received";
var HABIT_EVENTS = ["report_opened", "fix_context_pulled", "fix_dispatched"];
function isValidEventName(name) {
  return typeof name === "string" && EVENT_NAME_RE.test(name);
}
function sanitizeEventProperties(input, opts = {}) {
  const out = {};
  const dropped = [];
  if (!input || typeof input !== "object" || Array.isArray(input)) return { properties: out, dropped };
  const allow = new Set(opts.allowlist ?? []);
  let keys = 0;
  for (const [rawKey, rawValue] of Object.entries(input)) {
    if (keys >= EVENT_PROPERTY_LIMITS.maxKeys) {
      dropped.push(rawKey);
      continue;
    }
    const key = rawKey.slice(0, EVENT_PROPERTY_LIMITS.maxKeyLength);
    if (key.startsWith(RESERVED_PROPERTY_PREFIX) && !opts.allowReserved) {
      dropped.push(key);
      continue;
    }
    if (PII_PROPERTY_KEY_RE.test(key) && !allow.has(key)) {
      dropped.push(key);
      continue;
    }
    let value;
    if (rawValue === null || typeof rawValue === "boolean") value = rawValue;
    else if (typeof rawValue === "number") value = Number.isFinite(rawValue) ? rawValue : null;
    else if (typeof rawValue === "string") {
      const s = rawValue.slice(0, EVENT_PROPERTY_LIMITS.maxValueLength);
      value = opts.scrub ? opts.scrub(s) : s;
    } else if (rawValue instanceof Date) value = rawValue.toISOString();
    else {
      dropped.push(key);
      continue;
    }
    out[key] = value;
    keys += 1;
  }
  return { properties: out, dropped };
}
function propertiesWithinByteLimit(properties) {
  try {
    return JSON.stringify(properties).length <= EVENT_PROPERTY_LIMITS.maxBytes;
  } catch {
    return false;
  }
}

// src/event-tracker.ts
var _client2 = null;
var _projectId = "";
var _anonId = null;
var _userId = null;
var _userTraits = null;
var _sdkVersion2;
var _config = defaults();
var _scrub;
var _getSessionId;
var _buffer = [];
var _consentBuffer = [];
var _consent = "granted";
var _sampledIn = true;
var _flushTimer = null;
var _initialized2 = false;
var _flushing = false;
var _pendingIdentify = false;
var _unsubscribeConsent2 = null;
var CONSENT_BUFFER_MAX = 50;
var SPILL_MAX = 200;
var SPILL_TTL_MS = 24 * 60 * 60 * 1e3;
function defaults() {
  return {
    sampleRate: 1,
    autoPageviews: false,
    flushIntervalMs: 5e3,
    surface: "web",
    propertyAllowlist: []
  };
}
function now2() {
  return (/* @__PURE__ */ new Date()).toISOString();
}
function spillKey() {
  return "mushi_events_spill_" + _projectId;
}
function isRetryableStatus(status) {
  return status === void 0 || status === 429 || status >= 500;
}
function analyticsIdKey() {
  return "mushi_analytics_id_" + _projectId;
}
function loadOrMintAnalyticsId() {
  try {
    const stored = localStorage.getItem(analyticsIdKey());
    if (stored) return stored;
  } catch {
  }
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : "a-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
function persistAnalyticsId() {
  if (!_anonId) return;
  try {
    if (localStorage.getItem(analyticsIdKey()) !== _anonId) localStorage.setItem(analyticsIdKey(), _anonId);
  } catch {
  }
}
function hashToUnit(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return (h >>> 0) / 4294967296;
}
function readSpill() {
  try {
    const raw = localStorage.getItem(spillKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.at > SPILL_TTL_MS) {
      localStorage.removeItem(spillKey());
      return [];
    }
    return Array.isArray(parsed.events) ? parsed.events.slice(0, SPILL_MAX) : [];
  } catch {
    return [];
  }
}
function writeSpill(events) {
  try {
    if (events.length === 0) {
      localStorage.removeItem(spillKey());
      return;
    }
    localStorage.setItem(spillKey(), JSON.stringify({ at: Date.now(), events: events.slice(-SPILL_MAX) }));
  } catch {
  }
}
function takeSpill() {
  const events = readSpill();
  if (events.length > 0) writeSpill([]);
  return events;
}
function buildPayload2(events) {
  return {
    anon_id: _anonId,
    user_id: _userId,
    user_traits: _userTraits,
    session_id: _getSessionId ? _getSessionId() : null,
    sdk_version: _sdkVersion2,
    surface: _config.surface,
    events
  };
}
async function flushNow() {
  if (!_client2 || _flushing || _consent !== "granted") return;
  if (_buffer.length === 0 && !_pendingIdentify) return;
  _flushing = true;
  persistAnalyticsId();
  const batch = _buffer.splice(0, EVENT_PROPERTY_LIMITS.maxServerBatch);
  if (_pendingIdentify) {
    batch.unshift({ name: "identify", ts: now2(), properties: {} });
    _pendingIdentify = false;
  }
  try {
    const res = await _client2.postProductEvents(buildPayload2(batch));
    if (res.ok) {
      writeSpill([]);
    } else if (isRetryableStatus(res.error?.status)) {
      writeSpill([...readSpill(), ...batch.filter((e) => e.name !== "identify")]);
    }
  } catch {
    writeSpill([...readSpill(), ...batch.filter((e) => e.name !== "identify")]);
  } finally {
    _flushing = false;
  }
  if (_buffer.length >= EVENT_PROPERTY_LIMITS.maxClientBatch) void flushNow();
}
function enqueue(ev) {
  if (_consent === "denied") return false;
  if (_consent === "pending") {
    if (_consentBuffer.length >= CONSENT_BUFFER_MAX) return false;
    _consentBuffer.push(ev);
    return true;
  }
  _buffer.push(ev);
  if (_buffer.length >= EVENT_PROPERTY_LIMITS.maxClientBatch) void flushNow();
  return true;
}
function applyConsent(state) {
  if (state === "denied") {
    _consent = "denied";
    _buffer = [];
    _consentBuffer = [];
    _pendingIdentify = false;
    writeSpill([]);
    return;
  }
  const wasPending = _consent === "pending";
  _consent = "granted";
  if (wasPending) {
    _buffer.push(...takeSpill(), ..._consentBuffer);
    _consentBuffer = [];
  }
  if (_buffer.length > 0 || _pendingIdentify) void flushNow();
}
function stripUndefined(o) {
  const out = {};
  for (const [k, v] of Object.entries(o)) {
    if (v !== void 0) out[k] = v;
  }
  return out;
}
function isEventTrackingActive() {
  return _initialized2 && _consent === "granted" && _sampledIn;
}
function getEventAnonymousId() {
  if (!_initialized2 || _consent === "denied") return null;
  return _anonId;
}
function initEventTracker(opts) {
  if (_initialized2) return;
  if (typeof window === "undefined") return;
  const cfg = opts.config ?? {};
  _config = { ...defaults(), ...stripUndefined(cfg), propertyAllowlist: cfg.propertyAllowlist ?? [] };
  _projectId = opts.projectId;
  if (analyticsBlockReason(cfg) !== null) return;
  _initialized2 = true;
  _client2 = opts.client;
  _anonId = opts.anonId ?? loadOrMintAnalyticsId();
  _userId = opts.userId ?? null;
  _sdkVersion2 = opts.sdkVersion;
  _scrub = opts.scrub;
  _getSessionId = opts.getSessionId;
  const rate = Math.min(1, Math.max(0, _config.sampleRate));
  _sampledIn = rate >= 1 ? true : rate <= 0 ? false : hashToUnit(_anonId ?? "anon") < rate;
  if (!_sampledIn) return;
  _consent = resolveAnalyticsConsent(_projectId, cfg);
  if (_consent === "granted") _buffer.push(...takeSpill());
  else if (_consent === "denied") writeSpill([]);
  _unsubscribeConsent2 = onAnalyticsConsentChange(applyConsent);
  const interval = Math.max(1e3, _config.flushIntervalMs);
  _flushTimer = setInterval(() => {
    void flushNow();
  }, interval);
  const onHide = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") void flushNow();
  };
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onHide, { passive: true });
  window.addEventListener("pagehide", () => {
    void flushNow();
  }, { passive: true });
  if (_config.autoPageviews) patchHistory();
}
function trackEvent(name, properties, opts) {
  if (!_initialized2 || !_sampledIn) return false;
  if (!isValidEventName(name)) return false;
  const { properties: props } = sanitizeEventProperties(properties, {
    allowlist: _config.propertyAllowlist,
    scrub: _scrub
  });
  let merged = props;
  if (opts?.reserved) {
    const { properties: res } = sanitizeEventProperties(opts.reserved, { allowReserved: true, scrub: _scrub });
    merged = { ...props, ...res };
  }
  merged.$surface = _config.surface;
  if (typeof location !== "undefined" && merged.$route === void 0) merged.$route = location.pathname;
  if (!propertiesWithinByteLimit(merged)) return false;
  return enqueue({
    name,
    ts: opts?.ts ?? now2(),
    properties: merged,
    ...opts?.dedupKey ? { dedup_key: opts.dedupKey } : {}
  });
}
function setEventConsent(state) {
  if (!_initialized2) _consent = state;
  setAnalyticsConsent(_projectId, state);
}
function updateEventIdentity(userId, traits) {
  _userId = userId;
  _userTraits = traits ?? null;
  if (!_initialized2 || !_sampledIn || !userId || _consent === "denied") return;
  _pendingIdentify = true;
  if (_consent === "granted") void flushNow();
}
function flushEvents() {
  return flushNow();
}
function destroyEventTracker() {
  if (_flushTimer != null) {
    clearInterval(_flushTimer);
    _flushTimer = null;
  }
  _unsubscribeConsent2?.();
  _unsubscribeConsent2 = null;
  _initialized2 = false;
  _client2 = null;
  _buffer = [];
  _consentBuffer = [];
  _consent = "granted";
  _sampledIn = true;
  _pendingIdentify = false;
  _userId = null;
  _userTraits = null;
  _config = defaults();
}
var _historyPatched2 = false;
function patchHistory() {
  if (_historyPatched2 || typeof history === "undefined") return;
  _historyPatched2 = true;
  const original = history.pushState;
  history.pushState = function(...args) {
    const result = original.apply(this, args);
    trackEvent("page_view");
    return result;
  };
  window.addEventListener("popstate", () => {
    trackEvent("page_view");
  }, { passive: true });
}

// src/rate-limiter.ts
var DEFAULT_MAX_BURST = 10;
var DEFAULT_REFILL_RATE = 1;
var DEFAULT_REFILL_INTERVAL_MS = 5e3;
function createRateLimiter(config = {}) {
  const {
    maxBurst = DEFAULT_MAX_BURST,
    refillRate = DEFAULT_REFILL_RATE,
    refillIntervalMs = DEFAULT_REFILL_INTERVAL_MS
  } = config;
  let tokens = maxBurst;
  let lastRefill = Date.now();
  function refill() {
    const now3 = Date.now();
    const elapsed = now3 - lastRefill;
    const refills = Math.floor(elapsed / refillIntervalMs);
    if (refills > 0) {
      tokens = Math.min(maxBurst, tokens + refills * refillRate);
      lastRefill = now3;
    }
  }
  function tryConsume() {
    refill();
    if (tokens > 0) {
      tokens--;
      return true;
    }
    return false;
  }
  function reset() {
    tokens = maxBurst;
    lastRefill = Date.now();
  }
  function availableTokens() {
    refill();
    return tokens;
  }
  return { tryConsume, reset, availableTokens };
}

// src/pii-patterns.json
var pii_patterns_default = {
  patterns: [
    { id: "ssn", configKey: "ssns", source: "\\b\\d{3}-\\d{2}-\\d{4}\\b", replacement: "[REDACTED_SSN]" },
    { id: "creditCard", configKey: "creditCards", source: "\\b(?:\\d[ -]*){12,18}\\d\\b", replacement: "[REDACTED_CC]" },
    { id: "awsAccessKey", configKey: "secretTokens", source: "\\b(?:AKIA|ASIA)[0-9A-Z]{16}\\b", replacement: "[REDACTED_AWS_KEY]" },
    { id: "awsSecretKey", configKey: "secretTokens", source: `(?:aws_secret_access_key|secret_access_key)["'\\s:=]+[A-Za-z0-9/+=]{40}\\b`, caseInsensitive: true, replacement: "aws_secret_access_key=[REDACTED_AWS_SECRET]" },
    { id: "stripeSecretKey", configKey: "secretTokens", source: "\\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]{24,}\\b", replacement: "[REDACTED_STRIPE_KEY]" },
    { id: "stripePublishableKey", configKey: "secretTokens", source: "\\bpk_(?:live|test)_[A-Za-z0-9]{24,}\\b", replacement: "[REDACTED_STRIPE_PK]" },
    { id: "slackToken", configKey: "secretTokens", source: "\\bxox[abpor]-[A-Za-z0-9-]{10,}\\b", replacement: "[REDACTED_SLACK_TOKEN]" },
    { id: "githubPatClassic", configKey: "secretTokens", source: "\\bghp_[A-Za-z0-9]{36}\\b", replacement: "[REDACTED_GITHUB_PAT]" },
    { id: "githubPatFineGrained", configKey: "secretTokens", source: "\\bgithub_pat_[A-Za-z0-9_]{80,}\\b", replacement: "[REDACTED_GITHUB_PAT]" },
    { id: "openaiKey", configKey: "secretTokens", source: "\\bsk-(?:proj-)?[A-Za-z0-9_-]{20,}\\b", replacement: "[REDACTED_OPENAI_KEY]" },
    { id: "anthropicKey", configKey: "secretTokens", source: "\\bsk-ant-[A-Za-z0-9_-]{20,}\\b", replacement: "[REDACTED_ANTHROPIC_KEY]" },
    { id: "googleKey", configKey: "secretTokens", source: "\\bAIza[0-9A-Za-z_-]{35}\\b", replacement: "[REDACTED_GOOGLE_KEY]" },
    { id: "jwt", configKey: "secretTokens", source: "\\beyJ[A-Za-z0-9_-]+\\.[A-Za-z0-9_-]+\\.[A-Za-z0-9_-]+\\b", replacement: "[REDACTED_JWT]" },
    { id: "email", configKey: "emails", source: "\\b[A-Za-z0-9._%+\\-]+@[A-Za-z0-9.\\-]+\\.[A-Za-z]{2,}\\b", replacement: "[REDACTED_EMAIL]" },
    { id: "phone", configKey: "phones", source: "(?:\\+\\d{1,3}[\\s.-])?\\(?\\d{2,4}\\)?[\\s.-]\\d{3,4}[\\s.-]\\d{3,4}\\b", replacement: "[REDACTED_PHONE]" },
    { id: "ipv4", configKey: "ipAddresses", source: "\\b(?:\\d{1,3}\\.){3}\\d{1,3}\\b", replacement: "[REDACTED_IP]", defaultOn: false },
    { id: "ipv6", configKey: "ipv6", source: "\\b(?:[A-Fa-f0-9]{1,4}:){2,7}[A-Fa-f0-9]{0,4}\\b", replacement: "[REDACTED_IPV6]", defaultOn: false }
  ]
};

// src/pii-scrubber.ts
var RAW_PATTERNS = pii_patterns_default.patterns;
var ORDERED_PATTERNS = RAW_PATTERNS.map((p) => ({
  key: p.configKey,
  regex: new RegExp(p.source, p.caseInsensitive ? "gi" : "g"),
  replacement: p.replacement
}));
var DEFAULT_CONFIG = RAW_PATTERNS.reduce((acc, p) => {
  acc[p.configKey] = p.defaultOn !== false;
  return acc;
}, {});
function createPiiScrubber(config = {}) {
  const merged = { ...DEFAULT_CONFIG, ...config };
  const activePatterns = ORDERED_PATTERNS.filter((p) => merged[p.key]);
  function scrub(text) {
    if (!text) return text;
    let result = text;
    for (const { regex, replacement } of activePatterns) {
      result = result.replace(new RegExp(regex.source, regex.flags), replacement);
    }
    return result;
  }
  function scrubObject(obj, keys) {
    const copy = { ...obj };
    for (const key of keys) {
      if (typeof copy[key] === "string") {
        copy[key] = scrub(copy[key]);
      }
    }
    return copy;
  }
  return { scrub, scrubObject };
}
function scrubPii(text, config) {
  return createPiiScrubber(config).scrub(text);
}
var SENSITIVE_QUERY_KEY_SUBSTRING_RE = /token|jwt|passw|pwd|secret|api[-_]?key|apikey|auth|session|signature|email|phone|ssn/i;
var SENSITIVE_QUERY_KEY_EXACT_RE = /^(?:key|code|sig)$/i;
function isSensitiveQueryKey(key) {
  let decoded = key;
  try {
    decoded = decodeURIComponent(key);
  } catch {
  }
  return SENSITIVE_QUERY_KEY_SUBSTRING_RE.test(decoded) || SENSITIVE_QUERY_KEY_EXACT_RE.test(decoded);
}
function scrubQueryPairs(query, scrub) {
  return query.split("&").map((pair) => {
    const eq = pair.indexOf("=");
    if (eq === -1) return pair;
    const key = pair.slice(0, eq);
    const value = pair.slice(eq + 1);
    if (value.length === 0) return pair;
    if (isSensitiveQueryKey(key)) return `${key}=[Scrubbed]`;
    let decoded = value;
    try {
      decoded = decodeURIComponent(value);
    } catch {
    }
    const scrubbed = scrub(decoded);
    if (scrubbed !== decoded) return `${key}=${encodeURIComponent(scrubbed)}`;
    return pair;
  }).join("&");
}
function scrubUrl(url, config) {
  if (!url || !url.includes("?") && !url.includes("#")) return url;
  const { scrub } = createPiiScrubber(config);
  const scrubPart = (part) => {
    const qIdx = part.indexOf("?");
    if (qIdx === -1) return part;
    return part.slice(0, qIdx + 1) + scrubQueryPairs(part.slice(qIdx + 1), scrub);
  };
  const hashIdx = url.indexOf("#");
  if (hashIdx === -1) return scrubPart(url);
  return scrubPart(url.slice(0, hashIdx)) + scrubPart(url.slice(hashIdx));
}

// src/breadcrumbs.ts
var DEFAULT_MAX = 50;
var DEFAULT_MAX_MESSAGE = 500;
function createBreadcrumbBuffer(options = {}) {
  const max = Math.max(1, options.max ?? DEFAULT_MAX);
  const maxMsg = Math.max(50, options.maxMessageLength ?? DEFAULT_MAX_MESSAGE);
  let entries = [];
  return {
    add(input) {
      const ts = typeof input.timestamp === "number" ? input.timestamp : Date.now();
      const message = typeof input.message === "string" && input.message.length > maxMsg ? `${input.message.slice(0, maxMsg)}\u2026` : input.message;
      const crumb = {
        timestamp: ts,
        category: input.category,
        level: input.level ?? "info",
        message: message ?? "",
        ...input.data ? { data: input.data } : {}
      };
      entries.push(crumb);
      while (entries.length > max) entries.shift();
    },
    getAll() {
      return entries.slice();
    },
    clear() {
      entries = [];
    },
    size() {
      return entries.length;
    }
  };
}

// src/exception-normaliser.ts
var STACK_LIMIT = 8 * 1024;
var FALLBACK_JSON_LIMIT = 1e3;
function normaliseThrown(thrown) {
  if (thrown instanceof Error) {
    const name = thrown.name || "Error";
    const message = thrown.message || String(thrown);
    const stack = typeof thrown.stack === "string" && thrown.stack.length > 0 ? thrown.stack.slice(0, STACK_LIMIT) : void 0;
    const cause = thrown.cause;
    return {
      name,
      message,
      ...stack ? { stack } : {},
      ...cause !== void 0 ? { cause: cause instanceof Error ? cause.message : cause } : {}
    };
  }
  if (typeof thrown === "string") {
    return { name: "Error", message: thrown };
  }
  if (thrown && typeof thrown === "object") {
    const obj = thrown;
    const name = typeof obj.name === "string" ? obj.name : "Error";
    const message = typeof obj.message === "string" ? obj.message : (() => {
      try {
        return JSON.stringify(obj).slice(0, FALLBACK_JSON_LIMIT);
      } catch {
        return String(obj);
      }
    })();
    const stack = typeof obj.stack === "string" ? obj.stack.slice(0, STACK_LIMIT) : void 0;
    return { name, message, ...stack ? { stack } : {} };
  }
  if (thrown === void 0) return { name: "Error", message: "unknown" };
  return { name: "Error", message: String(thrown) };
}

// src/design-tokens.ts
var MUSHI_COLORS_LIGHT = {
  paper: "#F8F4ED",
  paperRaised: "#FFFFFF",
  ink: "#0E0D0B",
  inkMuted: "#5C5852",
  inkFaint: "#9A9489",
  inkDim: "#6E6760",
  rule: "rgba(14,13,11,0.10)",
  ruleStrong: "rgba(14,13,11,0.16)",
  accent: "#E03C2C",
  accentWash: "rgba(224,60,44,0.08)",
  accentInk: "#7A1F15",
  /** Aligned with brand.tokens.json `jade` (was Tailwind green-600 — drifted). */
  ok: "#2D7A55",
  /** Aligned with brand.tokens.json `viz-danger` (was Tailwind red-600 — drifted). */
  danger: "#B83232"
};
var MUSHI_COLORS_DARK = {
  paper: "#0F0E0C",
  paperRaised: "#1A1815",
  ink: "#F2EBDD",
  inkMuted: "#928B7E",
  inkFaint: "#5A5650",
  inkDim: "#7A7268",
  rule: "rgba(242,235,221,0.10)",
  ruleStrong: "rgba(242,235,221,0.18)",
  accent: "#FF5A47",
  accentWash: "rgba(255,90,71,0.12)",
  accentInk: "#FFE5E0",
  /** Aligned with brand.tokens.json `dark-jade`. */
  ok: "#3A9E6E",
  /** Aligned with brand.tokens.json `dark-viz-danger`. */
  danger: "#E05555"
};
var MUSHI_TIER_COLORS = {
  free: MUSHI_COLORS_LIGHT.inkMuted,
  explorer: "#2563EB",
  contributor: "#7C3AED",
  champion: "#D97706",
  default: MUSHI_COLORS_LIGHT.accent
};
var MUSHI_BANNER_NEON = {
  bg: "#0FFF50",
  fg: "#0a1a0a",
  border: "#00C43A"
};
var MUSHI_ON_ACCENT = "#FAF7F0";
var MUSHI_INVERSE = "#ffffff";
var MUSHI_CONTROL_DISABLED = {
  light: "#d1d1d6",
  dark: "#3a3a3c"
};
var MUSHI_SHADOW_INK = "#000000";
var MUSHI_ACCENT_SHADOW = {
  light: "#9A2A1E",
  dark: "#7A1F15"
};
var MUSHI_BANNER_BRAND_BORDER = {
  light: "#B52F1F",
  dark: "#C4321E"
};
var MUSHI_REPORTER_STATUS = {
  light: {
    sent: { fg: "#1E4A8C", bg: "rgba(30,74,140,0.08)", border: "rgba(30,74,140,0.16)" },
    review: { fg: "#8A5A00", bg: "rgba(180,120,0,0.10)", border: "rgba(180,120,0,0.18)" },
    fixing: { fg: "#9A3D12", bg: "rgba(224,60,44,0.10)", border: "rgba(224,60,44,0.18)" },
    fixed: { fg: "#1F6B3A", bg: "rgba(31,107,58,0.10)", border: "rgba(31,107,58,0.18)" }
  },
  dark: {
    sent: { fg: "#A8C4FF", bg: "rgba(120,160,255,0.12)", border: "rgba(120,160,255,0.22)" },
    review: { fg: "#FFD27A", bg: "rgba(255,190,90,0.12)", border: "rgba(255,190,90,0.22)" },
    fixing: { fg: "#FFB899", bg: "rgba(255,120,60,0.12)", border: "rgba(255,120,60,0.24)" },
    fixed: { fg: "#8FE3B0", bg: "rgba(80,200,130,0.12)", border: "rgba(80,200,130,0.22)" }
  }
};
function mushiPalette(mode) {
  return mode === "dark" ? MUSHI_COLORS_DARK : MUSHI_COLORS_LIGHT;
}
var MUSHI_SPACING = {
  hairline: 2,
  tight: 4,
  snug: 8,
  comfy: 12,
  roomy: 16,
  lounge: 20,
  open: 24,
  wide: 32
};
var MUSHI_RADIUS = {
  none: 0,
  control: 4,
  card: 10,
  sheet: 16,
  pill: 999
};
var MUSHI_TYPE = {
  fontDisplay: `'Iowan Old Style', 'Palatino Linotype', 'Palatino', 'Book Antiqua', 'Cambria', Georgia, 'Times New Roman', serif`,
  fontBody: `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI Variable Display', 'Segoe UI', sans-serif`,
  fontMono: `ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, Consolas, 'Liberation Mono', monospace`,
  sizeBody: 14,
  sizeLabel: 12,
  sizeTitle: 16,
  lineBody: 1.55
};
var MUSHI_Z = {
  base: 99999,
  /** banner sits one below the trigger so an open FAB overlaps it */
  banner: 99998,
  /** report/assistant panel sits one above the trigger */
  panel: 1e5,
  /** element-selector overlay — always on top */
  overlay: 2147483647
};
var MUSHI_MOTION = {
  easeStamp: "cubic-bezier(0.22, 1, 0.36, 1)",
  durationFast: 200,
  durationPanel: 300
};
var MUSHI_DURATION = {
  /** Fastest — tooltip show, chip hover tint */
  instant: 120,
  /** Default micro-interaction — most hover / focus transitions */
  fast: 200,
  /** Button bloom, option-row wipe */
  base: 220,
  /** Panel enter / exit */
  panel: 300,
  /** Deliberate — success stamp press, page-level transitions */
  slow: 420,
  /** Stamp ring draw, long ambient animations */
  ring: 700
};
var MUSHI_BORDER = {
  hairline: 1,
  regular: 1.5,
  strong: 2,
  /** Hanko stamp border (FAB inset accent stripe, stamp ring) */
  stamp: 3
};
var MUSHI_OPACITY = {
  disabled: 0.4,
  muted: 0.6,
  scrim: 0.72,
  overlay: 0.88
};
var MUSHI_LETTER_SPACING = {
  tight: "-0.01em",
  normal: "0",
  label: "0.02em",
  stamp: "0.08em",
  wide: "0.16em"
};
var MUSHI_GEOMETRY = {
  bannerHeight: 36,
  fabSize: 52,
  /** edge-tab launcher width (px) */
  edgeTabWidth: 32,
  /** default gutter from the screen edge (added on top of safe-area insets) */
  gutter: 24,
  /** Web panel width (px) — matches shipped Shadow DOM widget */
  panelWidth: 360,
  /** max panel height before keyboard inset (px) */
  panelMaxHeight: 480,
  /** below this viewport width the panel becomes a full-width bottom sheet */
  panelSheetBreakpoint: 480
};
function safeWidgetHex(v) {
  return /^#[0-9a-fA-F]{3,8}$/.test(v) ? v : "";
}
function resolveWidgetAccent(mode, accent = "", accentText = "") {
  const pal = mushiPalette(mode);
  const safeAccent = safeWidgetHex(accent);
  const safeAccentText = safeWidgetHex(accentText);
  const resolvedAccent = safeAccent || pal.accent;
  const accentWash = safeAccent ? mode === "dark" ? `${resolvedAccent}1F` : `${resolvedAccent}14` : pal.accentWash;
  const accentInk = safeAccentText || pal.accentInk;
  return { accent: resolvedAccent, accentWash, accentInk };
}
var MUSHI_COPY = {
  bannerLabel: "Beta",
  bannerMessage: "Found a bug? Tap to report it.",
  bugCta: "Report a bug",
  featureCta: "Feature request",
  triggerText: "\u{1F41B}",
  panelTitle: "Send feedback",
  myReportsTab: "My reports",
  assistantTab: "Ask"
};
function mushiTokens(mode) {
  return {
    mode,
    colors: mushiPalette(mode),
    spacing: MUSHI_SPACING,
    radius: MUSHI_RADIUS,
    type: MUSHI_TYPE,
    z: MUSHI_Z,
    motion: MUSHI_MOTION,
    duration: MUSHI_DURATION,
    geometry: MUSHI_GEOMETRY,
    copy: MUSHI_COPY
  };
}

// src/identity.ts
var MUSHI_IDENTITY_TOKEN_PREFIX = "mushi.identity";
function buildIdentityClaims(input) {
  const now3 = Math.floor(Date.now() / 1e3);
  const ttl = input.ttlSeconds && input.ttlSeconds > 0 ? Math.min(input.ttlSeconds, 3600) : 600;
  return {
    projectId: input.projectId,
    sub: input.userId,
    ...input.email ? { email: input.email } : {},
    ...input.name ? { name: input.name } : {},
    iat: now3,
    exp: now3 + ttl
  };
}
function base64UrlDecode(segment) {
  const pad = segment.length % 4 === 0 ? "" : "=".repeat(4 - segment.length % 4);
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/") + pad;
  if (typeof atob === "function") return atob(base64);
  const B = globalThis.Buffer;
  if (B) return B.from(base64, "base64").toString("binary");
  return "";
}
function parseIdentityToken(token) {
  if (typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const json = base64UrlDecode(parts[1]);
    const payload = JSON.parse(json);
    const projectId = typeof payload.projectId === "string" ? payload.projectId : void 0;
    const sub = typeof payload.sub === "string" ? payload.sub : void 0;
    if (!projectId || !sub) return null;
    return {
      projectId,
      sub,
      ...typeof payload.email === "string" ? { email: payload.email } : {},
      ...typeof payload.name === "string" ? { name: payload.name } : {},
      ...typeof payload.iat === "number" ? { iat: payload.iat } : {},
      ...typeof payload.exp === "number" ? { exp: payload.exp } : {}
    };
  } catch {
    return null;
  }
}

// src/favicon.ts
var MULTI_TENANT_PARENT_HOSTS = /* @__PURE__ */ new Set(["kensaur.us", "www.kensaur.us"]);
var SLUG_DOMAIN_HINTS = {
  "glot-it": "glot.it",
  glotit: "glot.it",
  "solo-boss-cloud": "solo-boss.kensaur.us",
  "mushi-mushi": "kensaur.us",
  "how-to-talk-to-girls": "talk.kensaur.us",
  htttg: "talk.kensaur.us",
  tsumagoi: "tsumagoi.kensaur.us"
};
var SLUG_ICON_URL_HINTS = {
  "mushi-mushi": "https://kensaur.us/mushi-mushi/admin/favicon.svg",
  "solo-boss-cloud": "https://solo-boss.kensaur.us/favicon.svg",
  "glot-it": "https://kensaur.us/glot-it/apple-touch-icon.png",
  glotit: "https://kensaur.us/glot-it/apple-touch-icon.png",
  "the-wanting-mind": "https://kensaur.us/the-wanting-mind/pwa-512x512.png",
  "how-to-talk-to-girls": "https://talk.kensaur.us/pwa-192.png",
  htttg: "https://talk.kensaur.us/pwa-192.png",
  tsumagoi: "https://tsumagoi.kensaur.us/apple-touch-icon.png",
  "help-her-take-photo": "https://kensaur.us/help-her-take-photo/assets/apple-touch-icon.png",
  "yen-yen": "https://kensaur.us/yen-yen/icon.svg"
};
var UNTRUSTED_FAVICON_HOSTS = [
  "www.google.com",
  "icons.duckduckgo.com",
  "icon.horse"
];
function isUntrustedFaviconUrl(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return UNTRUSTED_FAVICON_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}
function faviconUrlCandidates(domain, _size = 32) {
  return [
    `https://${domain}/favicon.svg`,
    `https://${domain}/favicon.ico`,
    `https://${domain}/favicon-32x32.png`,
    `https://${domain}/apple-touch-icon.png`
  ];
}
function resolveProjectFaviconDomains(source) {
  const out = [];
  const add = (d) => {
    const n = d?.trim().toLowerCase();
    if (!n || out.includes(n)) return;
    out.push(n);
  };
  add(githubRepoDomainHint(source.repo_url));
  add(SLUG_DOMAIN_HINTS[source.project_slug.toLowerCase()]);
  const fromOrigin = source.sdk_origin ? originToDomain(source.sdk_origin) : null;
  if (fromOrigin && !MULTI_TENANT_PARENT_HOSTS.has(fromOrigin)) {
    add(fromOrigin);
  }
  return out;
}
function projectFaviconUrlCandidates(source, size = 32) {
  if (source.icon_url?.trim()) return [source.icon_url.trim()];
  const seen = /* @__PURE__ */ new Set();
  const urls = [];
  const push = (url) => {
    if (!url || seen.has(url) || isUntrustedFaviconUrl(url)) return;
    seen.add(url);
    urls.push(url);
  };
  const slugHint = SLUG_ICON_URL_HINTS[source.project_slug.toLowerCase()];
  if (slugHint) push(slugHint);
  for (const domain of resolveProjectFaviconDomains(source)) {
    for (const url of faviconUrlCandidates(domain, size)) {
      push(url);
    }
  }
  return urls;
}
function originToDomain(origin) {
  const trimmed = origin.trim();
  if (!trimmed) return null;
  if (/^(capacitor|file|content|app):/i.test(trimmed)) return null;
  try {
    const url = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
    const host = url.hostname.toLowerCase();
    if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
      return null;
    }
    return host;
  } catch {
    return null;
  }
}
function githubRepoDomainHint(repoUrl) {
  if (!repoUrl?.trim()) return null;
  try {
    const u = new URL(repoUrl.trim());
    const host = u.hostname.toLowerCase();
    const parts = u.pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
    if (parts.length < 2) return null;
    const repo = parts[1].replace(/\.git$/i, "").toLowerCase();
    if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i.test(repo)) return repo;
    if (host === "github.com" && repo.endsWith(".github.io")) return repo;
    if (host !== "github.com" && host !== "www.github.com") return host;
    return null;
  } catch {
    return null;
  }
}
function resolveProjectDomain(source) {
  return resolveProjectFaviconDomains(source)[0] ?? null;
}
function isLikelyGenericFavicon(img) {
  if (typeof document === "undefined") return false;
  if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) return false;
  try {
    const canvas = document.createElement("canvas");
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;
    ctx.drawImage(img, 0, 0);
    const { data } = ctx.getImageData(0, 0, w, h);
    let opaque = 0;
    let satSum = 0;
    let maxDiff = 0;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a < 128) continue;
      opaque++;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const diff = max - min;
      maxDiff = Math.max(maxDiff, diff);
      satSum += diff;
    }
    if (opaque === 0) return true;
    const avgSat = satSum / opaque;
    return avgSat < 14 && maxDiff < 24;
  } catch {
    return isUntrustedFaviconUrl(img.src);
  }
}
function projectInitials(name) {
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  const compact = name.replace(/[^a-zA-Z0-9]/g, "");
  return (compact.slice(0, 2) || name.slice(0, 2) || "?").toUpperCase();
}
var INITIALS_CHIP_THEMES = [
  "info",
  "brand",
  "warn",
  "ok",
  "accent"
];
function projectInitialsThemeIndex(projectId) {
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = (hash + projectId.charCodeAt(i)) % INITIALS_CHIP_THEMES.length;
  }
  return hash;
}
function readPageFaviconHref() {
  if (typeof document === "undefined") return null;
  const selectors = [
    'link[rel="icon"]',
    'link[rel="shortcut icon"]',
    'link[rel="apple-touch-icon"]'
  ];
  for (const sel of selectors) {
    const link = document.querySelector(sel);
    const href = link?.href;
    if (href && isSafeFaviconHref(href)) return href;
  }
  return null;
}
function isSafeFaviconHref(href) {
  try {
    const base = typeof location !== "undefined" ? location.href : void 0;
    const protocol = new URL(href, base).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

export { ACTIVATION_EVENT, DEFAULT_API_ENDPOINT, DEFAULT_MAX_RETRIES, DEFAULT_TIMEOUT, EVENT_NAME_RE, EVENT_PROPERTY_LIMITS, HABIT_EVENTS, MAX_REPORT_PAYLOAD_BYTES, MAX_SCREENSHOT_DATA_URL_BYTES, MUSHI_ACCENT_SHADOW, MUSHI_BANNER_BRAND_BORDER, MUSHI_BANNER_NEON, MUSHI_BEACON_MAX_BYTES, MUSHI_BORDER, MUSHI_COLORS_DARK, MUSHI_COLORS_LIGHT, MUSHI_CONTROL_DISABLED, MUSHI_COPY, MUSHI_DURATION, MUSHI_EVENTS, MUSHI_EVENT_NAMES, MUSHI_GEOMETRY, MUSHI_IDENTITY_TOKEN_PREFIX, MUSHI_INTERNAL_HEADER, MUSHI_INTERNAL_INIT_MARKER, MUSHI_INVERSE, MUSHI_LETTER_SPACING, MUSHI_MOTION, MUSHI_ON_ACCENT, MUSHI_OPACITY, MUSHI_PROJECT_HEADER, MUSHI_RADIUS, MUSHI_REPORTER_STATUS, MUSHI_SDK_PACKAGE_HEADER, MUSHI_SDK_VERSION_HEADER, MUSHI_SHADOW_INK, MUSHI_SPACING, MUSHI_SURFACES, MUSHI_TIER_COLORS, MUSHI_TYPE, MUSHI_USER_TOKEN_HEADER, MUSHI_Z, REGION_ENDPOINTS, analyticsBlockReason, buildBeaconEnvelope, buildIdentityClaims, buildSdkIngestHeaders, captureEnvironment, checkReportPayloadSize, createApiClient, createBreadcrumbBuffer, createLogger, createOfflineQueue, createPiiScrubber, createPreFilter, createRateLimiter, destroyEventTracker, destroySessionTracker, diagnoseEnvConfig, dntActive, estimateJsonBytes, expandPreset, extractTunnelPath, faviconUrlCandidates, flushEvents, flushLastOutboundOnUnload, formatBytes, getBackoffDelay, getDeviceFingerprintHash, getEventAnonymousId, getReporterToken, getSessionId, githubRepoDomainHint, hmacSha256Hex, initEventTracker, initSessionTracker, isAutomatedBrowser, isEventTrackingActive, isLikelyGenericFavicon, isMushiBeaconEnvelope, isPageUnloading, isSameOriginOrRelative, isUntrustedFaviconUrl, isValidEventName, markPageUnloading, matchesErrorFilter, mushiPalette, mushiTokens, newUuid, noopLogger, normaliseThrown, onAnalyticsConsentChange, originToDomain, parseIdentityToken, parseRetryAfter, projectFaviconUrlCandidates, projectInitials, projectInitialsThemeIndex, propertiesWithinByteLimit, readPageFaviconHref, resolveAnalyticsConsent, resolveEnvConfig, resolveProjectDomain, resolveProjectFaviconDomains, resolveRegionEndpoint, resolveRequestBaseUrl, resolveWidgetAccent, safeWidgetHex, sanitizeEventProperties, scrubPii, scrubUrl, sendOnUnload, setAnalyticsConsent, setEventConsent, sha256Hex, shouldDropCapturedError, trackEvent, trackPageView, updateEventIdentity, updateSessionIdentity, validateConfig };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map